import React, {ChangeEvent, CSSProperties, MouseEvent, ReactElement, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import './TaskItem.less';
import '@/stylesheets/form.less';
import '@/Case/CaseCard/CaseCard.less';
import * as TaskHelper from "./TaskHelper";
import {AWAITING_TITLE, getCost, handleCheckboxClick, TaskLike} from "./TaskHelper";
import {startTimer, stopTimer} from "./timers";
import * as TemplateTaskHelper from "./TemplateTasks/TemplateTaskHelper";
import Comments from "@/Comment/Comments";
import * as MiscActions from "@/store/actions/miscActions";
import * as TaskActions from "@/store/actions/taskActions";
import {confirmChangesMade, confirmWarning, confirmWarningOkCancel} from "@/ui/Confirm";
import ContentBlock, {ContentBlockChildProps} from "@/ui/ContentBlock/ContentBlock";
import ContentBlockContainer from "@/ui/ContentBlock/ContentBlockContainer";
import * as DateUtil from "@/lib/dateutils";
import IconButton from "@/ui/IconButton/IconButton";
import MultiSelect from '@/ui/MultiSelect';
import OkCancel from "@/ui/Buttons/OkCancel";
import Select, {SelectHandle} from '@/ui/Select/Select';
import Task, {AssignedToType, TaskNotification, TaskTieIns} from '@/entities/task';
import util from '@/lib/util';
import LowMedHigh from "@/ui/LowMedHigh/LowMedHigh";
import FormElement from "@/lib/FormElement";
import Checkbox, {CheckboxClickEvent} from "@/ui/Checkbox";
import ActivityLabel from "@/ui/ActivityLabel/ActivityLabel";
import DocumentBlock from "@/Documents/DocumentBlock";
import ActivityType from "@/entities/activityType";
import DurationEntry from "@/ui/DurationEntry/DurationEntry";
import Alert from "@/lib/alert";
import ContactSelect, {ContactSelectHandle} from "@/Contacts/ContactSelect/ContactSelect";
import CaseHelper from "@/Case/CaseHelper";
import LeadHelper from "@/CRM/Sales/LeadHelper";
import ScrollWithIndicator from "@/ui/ScrollWithIndicator/ScrollWithIndicator";
import CaseSelect from "@/Case/CaseSelect/CaseSelect";
import TextField from "@/ui/TextField";
import MuiTextField from '@mui/material/TextField';
import FinancialHelper from "@/Financial/FinancialHelper";
import OptionHelper from "@/Configuration/GeneralOptions/OptionHelper";
import NotificationFlag from '@/ui/Notification/NotificationFlag';
import Auth from '@/Auth/Auth';
import TaskTimer from './TaskTimer/TaskTimer';
import User from '@/entities/user';
import EntityMeta from '@/ui/EntityMeta/EntityMeta';
import * as CampaignHelper from '@/CRM/Marketing/Campaigns/CampaignHelper';
import TemplateTask from '@/entities/templateTask';
import InvoiceHelper from '@/Financial/Invoices/InvoiceHelper';
import {stripHtml} from 'string-strip-html';
import CaseFinancialSummary from '@/Case/CaseFinancialSummary/CaseFinancialSummary';
import DocketSelect from '@/ui/DocketSelect/DocketSelect';
import i18n from '@/i18n/i18n';
import HelpMessage from '@/i18n/HelpMessage';
import DecimalInput from '@/ui/Inputs/DecimalInput';
import MilestoneHelper from '@/CRM/Sales/Milestones/MilestoneHelper';
import failureLadder from '@/lib/failureLadder';
import {deleteImpactTask, deleteTask} from "@/store/deps/taskDeps";
import {formatFML, formatUserName, formatUserNameFromId} from "@/Contacts/ContactFormat";
import upFirstLetter from "@/lib/picos/upFirstLetter";
import {getCurrentFirmId, getCurrentUser, getCurrentUserId} from "@/lib/picos/currentLogin";
import {formatDuration, roundDurationToMinutes} from "@/lib/picos/duration";
import {getZeroUUID} from "@/lib/picos/uuids";
import newHRTimestamp from "@/lib/picos/timestamp";
import {distinctFilter, existsFilter} from "@/lib/picos/filters";
import Icons from "@/ui/IconButton/Icons";
import RoutedIconButton from "@/ui/Buttons/RoutedIconButton";
import {startEditingPageF, stopEditingPageF} from "@/lib/pageEditing";
import MultilineText, {MultilineTextRef} from "@/ui/MultilineText";
import EmojiBar from "@/ui/EmojiBar/EmojiBar";
import {deleteComment} from "@/store/deps/commentDeps";
import type {uuid} from "@/@types/entity";
import Contact from "@/entities/contact";
import type {RootState} from "@/@types/store";
import FlatFee from "@/entities/flatFee";
import Matter from "@/entities/matter";
import type {CancelFunction, OkFunction} from "@/@types/functions";
import {SearchPageProps} from "@/App";
import {EditableTableCardProps} from "@/ui/EditableTable/EditableTable";
import Lead from "@/entities/lead";
import Campaign from "@/entities/campaign";
import Milestone from "@/entities/milestone";
import FlatFeeMilestone from "@/entities/flatFeeMilestone";
import Group from "@/entities/group";
import deepEqual from "fast-deep-equal/es6";
import Category from "@/entities/category";
import type {InputActionMeta} from "react-select";
import type {SelectOption} from "@/@types/select";
import {Button} from "@mui/material";
import {PublishedWithChanges} from "@mui/icons-material";
import useOptions, {OptionNames} from "@/hooks/useOptions";
import debounce from "debounce";
import {useRootDispatch} from "@/hooks/useOutlawStore";
import useComponentCache from "@/hooks/useComponentCache";
import useStateWithCallback from "@/hooks/useStateWithCallback";
import useSubEditActive from "@/hooks/useSubEditActive";
import UserSelect from "@/ui/UserSelect/UserSelect";
import {canClaimTaskForTimerWork, hasTaskTimerStarted, TASK_ASSIGNEE_LOCKED_MESSAGE} from './taskTimerState';
import {HeldTaskBanner} from './HeldTaskNotice';

const checkBoxMarginLeft: CSSProperties = {marginLeft: '5px'};
const UNSET_HEIGHT: CSSProperties = {minHeight: 'unset', height: 'unset'};
const FLEX_NO_WRAP: CSSProperties = {flexDirection: 'row', flexWrap: 'nowrap'};

interface OverrideHandlers {
    [key: string]: (e: SyntheticEvent, data?: any) => void;
}

interface OverrideRefs {
    [key: string]: React.Ref<any>;
}

type FlatFeeSelectItem = SelectOption<string, { flatFee: FlatFee, milestone: FlatFeeMilestone | null }>

type TaskItemState = {
    current: TaskLike,
    fromTemplate: boolean,
    isContentExpanded: boolean,
    hasChanged: boolean,
    invoiceNotesCounter: number,
    width: number,
    manualTitle: boolean,
    blurCalled: boolean,
    timerChange: boolean
}

function TaskItem(props: TaskItemProps) {
    const {cacheSave, cacheValue, cacheReset} = useComponentCache<TaskItemState>(`task-item-${props.current?.id || props.newId}`);

    const dispatch = useRootDispatch();

    const optionHelper = useOptions();

    const newTask: Task | null = props.current ? null : new Task({
        taskId: props.newId,
        firmId: getCurrentFirmId(),
        addedBy: getCurrentUserId(),
        addedOn: newHRTimestamp(),
        assignedTo: getCurrentUserId(),
        status: 'C',
        dueDate: DateUtil.endOfDay(new Date(), true),
        tieIns: [],
        laborTime: 0,
        laborTimeOverride: 0,
        travelTime: 0,
        travelTimeOverride: 0,
        cost: 0,
        costOverride: 0,
        ...props.presets
    });

    const descriptionRef = useRef<MultilineTextRef>(null);
    const doResetRef = useRef<boolean>(false);

    const [current, setCurrent] = useState<TaskLike>(cacheValue('current', (props.current?.clone() || newTask)!));
    const [timerChange, setTimerChange] = useStateWithCallback(false);

    const task = current instanceof Task ? current : null;
    const assigneeLocked = !!task && hasTaskTimerStarted(task);
    const templateTask = current instanceof TemplateTask ? current : null;
    const isTemplate = current instanceof TemplateTask;
    const isTask = current instanceof Task;
    const unsavedEntity = props.env?.unsavedEntity;
    // Destructured to keep the prop-sync useEffect's deps narrow. Depending on `props` directly
    // would fire the effect on every Redux dispatch (connect() rebuilds the props ref for any
    // of the 18 mapStateToProps slices); referencing local destructured values lets the effect
    // fire only when these specific slices change. See 33ca945c3 / 36a264ac3 for history.
    const propsCurrent = props.current;
    const propsTasks = props.tasks;

    const tieIns = useMemo((): TaskTieIns[] => {
        return current instanceof Task ? current.tieIns ?? [] : []
    }, [current]);

    const matterTieIns = useMemo(() => {
        return tieIns
            .filter(ti => ti.module === 'Client' && ti.entity === 'Matter')
            .filter(ti => !!props.matters.get(ti.id) || (unsavedEntity?.id === ti.id))
            .map(ti => {
                // Get the matching matter
                const matter: Matter | null = props.matters.get(ti.id) || (unsavedEntity?.id === ti.id ? unsavedEntity as Matter : null);

                if (!matter) return null;

                return {
                    label: matter?.name,
                    value: matter?.name,
                    matter
                };
            })
            .filter(existsFilter);
    }, [tieIns, props.matters, unsavedEntity]);

    /**
     * Get the default flat fee bucket for this task (if any)
     */
    const defaultFlatFee = useMemo((): uuid | undefined => {
        // Get all flat fees across related matters
        const allFlatFees = matterTieIns
            .map(ti => ti!.matter)
            .map(m => CaseHelper.getFlatFees(m))
            .flat()
            .filter(existsFilter);

        // Prefer an explicit default; fall back to the sole bucket if only one exists
        const explicit = allFlatFees.find(ff => ff?.isDefault);
        if (explicit) return explicit.id;
        if (allFlatFees.length === 1) return allFlatFees[0].id;
        return undefined;
    }, [matterTieIns]);

    const fromTemplateDefault = props.current instanceof Task
                                ? !!props.current?.fromTemplateId || !!props.presets?.title
                                : !!props.presets?.title;

    const [fromTemplate, setFromTemplate] = useState<boolean>(cacheValue('fromTemplate', fromTemplateDefault));
    const [isContentExpanded, setIsContentExpanded] = useState<boolean>(cacheValue('isContentExpanded', false));
    const [hasChanged, setHasChanged] = useStateWithCallback<boolean>(cacheValue('hasChanged', false));
    const [invoiceNotesCounter, setInvoiceNotesCounter] = useState<number>(cacheValue('invoiceNotesCounter', 1));
    const [isEditingTimer, setIsEditingTimer] = useState<boolean>(false);
    // Open-editor counter for DocumentBlock (and any inline editor) inside this
    // task. Centralised in useSubEditActive: never cached (a teardown-without-
    // onEndEdit must not stick >0 across remount) and clamped at 0. The
    // onBeginEdit/onEndEdit it returns are wired to DocumentBlock below.
    const {subEditActive, onBeginEdit, onEndEdit} = useSubEditActive();
    const [width, setWidth] = useState<number>(cacheValue('width', 0));
    const [manualTitle, setManualTitle] = useState<boolean>(cacheValue('manualTitle', !!(props.current && !props.current.fresh && props.current.title)));
    const [blurCalled, setBlurCalled] = useState<boolean>(false);

    // This should be in a useEffect to avoid unnecessary renders
    if (task && props.current && !task.title) task.dueDate = DateUtil.endOfDay(new Date(), true);

    const isNew = !props.current || props.current?.fresh;

    /**
     * Determine the rate for this task, based on user, default rates and case rates.
     * @param {Task} task
     * @param {Matter} [matter]
     * @return {number}
     */
    const getBillableRate = useCallback((task: TaskLike, matter?: Matter): number => {
        // Who is the user?
        const userOrGroup = TaskHelper.getAssigned(task);

        // If we got a matter passed through env, use that instead (or in addition) to any matters
        // found in clientMatters. Passing a matter to this function overrides the all.
        const matters = matter ? [matter]
                               : matterTieIns
                            .map(mti => mti!.matter)
                            .map(m => {
                                return (props.env?.matter?.id === m.id)
                                       ? props.env?.matter
                                       : m;
                            });

        // If there is an associated case, use the rate there
        const rates = matters
            .filter(m => m.hourlyRates?.length > 0) // only if hourly
            .map(m => m.hourlyRates) // now an array of hourly rates
            .flat() // now an array of hourly rates
            .filter(hr => hr?.id === userOrGroup?.id)  // only if this user/group is in the list!
            .map(hr => Number(hr.rate)); // Now we can extract the rates

        // If no entries, we use the default rate. Otherwise, we use the case-specified rate.
        return rates.length > 0 ? Math.max(...rates) : (userOrGroup?.billableRate ?? 0);
    }, [matterTieIns, props.env?.matter]);

    // Refs
    const clientsRef = React.createRef<ContactSelectHandle>();
    const formRef = React.createRef<HTMLFormElement>();
    const titleRef = React.createRef<SelectHandle<string, Task>>();
    const hasCanceledRef = useRef(false);
    // OWA-perf: counts how many times the prop-sync effect has swapped `current` from props
    // since this TaskItem mounted. High counts during a slow keystroke implicate Socket.IO churn.
    const propsCurrentChurnRef = useRef(0);

    const _callStorageWorker = useMemo(() => debounce((task: Task) => {
            if (!hasCanceledRef.current && isTask) {
                // OWA-perf: time the autosave call so we can spot bloated writes contributing to lag.
                const start = performance.now();
                window.storageWorker
                    .cacheItem(
                        getCurrentFirmId(),
                        getCurrentUserId(),
                        'task',
                        props.env?.topLevelId ?? getZeroUUID(),
                        task.id,
                        task
                    )
                    .then(() => {
                        const elapsed = performance.now() - start;
                        if (elapsed > 100) {
                            let size = 0;
                            try { size = JSON.stringify(task).length; } catch { /* ignore */ }
                            console.warn('[invoiceNotes-lag] slow-autosave', {
                                elapsedMs: Math.round(elapsed),
                                taskId: task.id,
                                size,
                            });
                        }
                    })
                    .catch((err: any) => Alert.addSimpleError(err.message));
            }
        }, 1000, {immediate: false}),
        [isTask, props.env?.topLevelId]
    );

    // Call the storage worker with the updated task
    const callStorageWorker = useCallback((task: Task) => {
            if (task) _callStorageWorker(task);
        }, [_callStorageWorker]
    );

    // Cancel any pending debounced autosave write. Called by save/cancel/delete handlers
    // before triggering cleanup so the trailing-edge debounce can't fire post-cleanup and
    // resurrect the autosave entry as bloat.
    const clearStorageWorker = useCallback(() => {
            _callStorageWorker.clear?.();
        }, [_callStorageWorker]
    );

    // componentDidUpdate(prevProps, prevState, ss) {
    // 	const isTemplate = props.current instanceof TemplateTask;
    //
    // 	// Check for SAVE_OPEN_TASK state
    // 	const quickAction = props.quickAction;
    //
    // 	// There is a task to show and it hasn't been saved yet.  Let's call onsubmit.
    // 	if (quickAction && quickAction.taskToShow && !quickAction.oldTaskSaved) {
    // 		onClick();
    // 	}
    //
    // 	// Check for external changes
    // 	else if (props.current && (props.current.addedOn > current.addedOn)) { //&& (props.current.addedBy !== getCurrentUserId())) {
    // 		// Which user made the change?
    // 		const user = props.users.get(props.current.addedBy);
    // 		const userName = formatUserName(user);
    // 		console.log('external changes detected', userName, {
    // 			current: !!props.current,
    // 			addedOnGtState: props.current.addedOn > current.addedOn,
    // 		});
    //
    // 		// If we haven't updated anything yet, just apply the changes
    // 		if (!prevState.hasChanged) {
    // 			console.log('prevState had not changed')
    // 			// Accept theirs
    // 			const task = isTemplate
    // 			             ? props.current.clone()
    // 			             : updateCost({}, props.current.clone());
    //
    // 			saveState({task, hasChanged: false});
    // 		}
    // 		else if (!fromTemplate) {
    // 			confirmWarningOkCancel(
    // 				'External Changes Detected',
    // 				(
    // 					<div>
    // 						<p><strong>{userName}</strong> has made changes to this task. Do you want to apply their
    // 							changes, or ignore them?</p>
    // 						<p>If you apply their changes, you will lose any changes you have made. If you ignore their
    // 							changes and save your own, you will overwrite their changes.</p>
    // 					</div>
    // 				),
    // 				'Apply Changes', 'Ignore',
    // 				() => {
    // 					// Accept theirs
    // 					const task = isTemplate
    // 					             ? props.current.clone()
    // 					             : updateCost({}, props.current.clone());
    //
    // 					saveState({task, hasChanged: false});
    // 				},
    // 				() => {
    // 				}
    // 			);
    // 		}
    // 	}
    //
    // 	// This indicated that a change was made, by the current user, but outside of this program
    // 	else if (props.current && (props.current.addedOn > current.addedOn) && (props.current.addedBy === getCurrentUserId())) {
    // 		const task = isTemplate
    // 		             ? props.current.clone()
    // 		             : updateCost({}, props.current.clone());
    //
    //      setHasChanged(true);
    //      setCurrent(task);
    // 	}
    // 	else {
    // 		current = isTemplate ? current : updateCost({}, current);
    // 	}
    // }

    /**
     * Resize event. Check the height and width and set mobile or desktop view.  This can determine which columns are shown.
     *
     * We now care about the height, so that we can do virtualized row rendering.
     */
    const onResize = useCallback(() => {
            const element = formRef.current;

            if (element && (element.clientWidth !== width)) {
                setWidth(element.clientWidth);
            }
        }, [formRef, setWidth, width]
    );

    // If the value changes in props, we need to reset the "current" state, as somebody else
    // (maybe even us) has changed the task.
    // There are two ways this can happen:
    // 1. Another user has changed the task since we opened it.  If that is the case, check
    //    to see if we have made any edits.
    //    a. If yes, prompt the user to accept or reject their changes.
    //    b. If not, just swap the current state with this one.
    // 2. If we just rolled back or restored to a previous version, we should just replace the state.
    //    We know if the task has been rolled back because the restore field will be true
    useEffect(() => {
        // Never call this if this is a new task.
        if (isNew) return;

        // Skip if we are in the process of closing/deleting
        if (hasCanceledRef.current) return;

        // Get the latest version from props.
        if (propsCurrent) { // Can't change if it is brand new
            const latestVersion = propsTasks.get(propsCurrent.id);
            if (!latestVersion) return;

            const isDifferent = latestVersion.dbAddedOn !== current.dbAddedOn;
            const sameUser = latestVersion.addedBy === getCurrentUserId();

            // If they are different, we need to update this task.
            if (isDifferent) {
                // A running version is adopted like any other incoming version: a clean editor shows it, and an editor
                // holding a draft keeps the draft. (Until OWA-998 the editor saved a stopped copy here, which stopped
                // timers started in this tab or another one; John approved removing it on 2026-10-11.)
                if (hasChanged) return;
                else {
                    // Has not changed, so just update the state.
                    // Re-check isDifferent inside the deferred callback to guard against
                    // stale closures when multiple dispatches interleave before setTimeout fires.
                    propsCurrentChurnRef.current += 1;
                    setTimeout(() => {
                        if (latestVersion.dbAddedOn !== current.dbAddedOn) {
                            // The invoiceNotes MuiTextField is uncontrolled (defaultValue)
                            // for typing-perf reasons — see d6afc128e. Bump the remount key
                            // when the incoming version's text differs so the DOM picks up
                            // the new value. Gated on the !hasChanged branch we're inside,
                            // so we never clobber in-progress typing.
                            if (latestVersion.invoiceNotes !== current.invoiceNotes) {
                                setInvoiceNotesCounter(c => c + 1);
                            }
                            setCurrent(latestVersion);
                            setHasChanged(false);
                        }
                    }, 0);

                    // If the change was made by someone other than us, let the user know.
                    // Branch on the actual source so we don't mislead a single user into thinking
                    // another human edited the task. Cascade saves from delete/merge dependency
                    // handlers stamp importSource='delete'|'merge'; importers stamp their own source.
                    if (!sameUser) {
                        const importSource = latestVersion.importSource;
                        let message: string;
                        if (importSource === 'delete' || importSource === 'merge') {
                            message = `This task was updated by a recent ${importSource} cascade. The latest values are now shown.`;
                        } else if (importSource) {
                            message = `This task was updated by an import (${importSource}). The latest values are now shown.`;
                        } else {
                            const otherName = formatUserNameFromId(latestVersion.addedBy) || 'Another user';
                            message = `${otherName} has made changes to this task, which are now reflected here.`;
                        }
                        Alert.add(new Alert(
                            'info',
                            latestVersion.id,
                            latestVersion.addedOn,
                            message
                        ));
                    }
                }
            }
        }
    }, [current, dispatch, propsCurrent, propsTasks, hasChanged, setHasChanged, isNew]);

    // Save values after changes made
    useEffect(() => {
        return () => {
            const doReset = doResetRef.current;

            if (doReset) {
                cacheReset();
                return;
            }

            // Transient UI state always survives a remount in the same tab.
            const transient = {
                fromTemplate,
                isContentExpanded,
                hasChanged,
                invoiceNotesCounter,
                width,
                manualTitle,
                blurCalled,
                timerChange
            };

            // Only cache `current` when there are genuine unsaved edits.
            // Caching a CLEAN (unmodified) entity restores a stale snapshot on
            // the next mount, diverging from fresh Redux/Socket.IO state (see
            // the May-2026 InvoiceCard "three different totals" bug and the
            // componentCache rule in client/CLAUDE.md). cacheSave OVERWRITES the
            // whole entry, so omitting `current` here means the next mount's
            // `cacheValue('current', props.current?.clone() || newTask)` falls
            // back to the fresh prop. When `hasChanged` is true we DO cache
            // `current` so the user's in-progress draft is preserved.
            if (hasChanged) cacheSave({current, ...transient} as TaskItemState);
            else cacheSave(transient as TaskItemState);
        }
    }, [current, fromTemplate, isContentExpanded, hasChanged, invoiceNotesCounter, width, manualTitle, blurCalled, timerChange, cacheSave, cacheReset]);

    // If not a template, cache changes to the task whenever it changes
    useEffect(() => {
        if (!isTemplate && task) {
            // If we are in associatedTasks, there will be a props.env.topLevelId. Otherwise we are in the grid, and
            // we can mark the parentId as null
            callStorageWorker(task);
        }
    }, [callStorageWorker, isTemplate, task]);

    // OWA-339: Ensure the cost of the task is always up to date.
    // IMPORTANT: Skip running tasks because getCost() uses Date.now() for elapsed time calculation,
    // which returns a different value on every call. This would cause an infinite re-render loop:
    // getCost differs → setCurrent → task changes → effect re-runs → getCost differs again → loop
    const costUpdatePendingRef = useRef(false);
    useEffect(() => {
        // Skip if: no task, already updating, or task is running (constantly changing cost)
        if (!task || costUpdatePendingRef.current || task.running) return;

        const calculatedCost = getCost(task);
        if (calculatedCost !== task.cost) {
            costUpdatePendingRef.current = true;
            const taskClone = task.clone();
            taskClone.cost = calculatedCost;
            setCurrent(taskClone);
            // Reset after the state update cycle completes
            queueMicrotask(() => { costUpdatePendingRef.current = false; });
        }
    }, [setCurrent, task]);

    // Handle listeners and cache resetting
    useEffect(() => {
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, [onResize]);

    // If the task is associated with a matter, and has no flat fee, check to see if it should and if so update it
    useEffect(() => {
        if (!!task && !task.flatFeeBucket && defaultFlatFee) {
            const taskClone = task.clone();
            taskClone.flatFeeBucket = defaultFlatFee;
            setCurrent(taskClone);
        }
    }, [defaultFlatFee, setCurrent, task]);

    const convertToTemplate = useCallback(() => {
        TaskHelper.convertToTemplate(current as Task);
    }, [current]);

    /**
     * TODO: This should be memoized, but we can't guarantee that getCurrentUser will return non-null.
     */
    const getCategoriesForUser = useCallback((): Category[] => {
        // Alphabetize
        if (getCurrentUser() && props.categories) {
            return props.categories
                .filter(c => {
                    return (c.addedBy === getCurrentUserId() || c.entityType === 'category') && c.status !== 'D'
                })
                .sort((a, b) => {
                    const aRelated = a.relatedAreas.sort();
                    const bRelated = b.relatedAreas.sort();

                    if (!aRelated.length || !bRelated.length) {
                        if (aRelated.length === 0 && bRelated.length === 0) return a.name.localeCompare(b.name);
                        else return bRelated.length - aRelated.length;
                    }
                    else return a.name.localeCompare(b.name);
                })
        }
        else return [];
    }, [props.categories]);

    // Used by ContentBlockContainer to let us know that a container is contracting/expanding
    const onBeforeCollapse = useCallback(() => {
        setIsContentExpanded(false);
    }, [setIsContentExpanded]);

    const onBeforeExpand = useCallback(() => {
        setIsContentExpanded(true);
    }, [setIsContentExpanded]);

    /**
     * Change in a text field.
     *
     * @param {KeyboardEvent} e
     * @param {string} field
     * @param {string} value
     */
    const onChange = useCallback((e: MouseEvent, field: string, value?: string) => {
        if (!e) {
            console.error('No event in onChange for ' + field);
            return;
        }

        e.preventDefault();

        let val: string | null | undefined = value;
        if (val === '') val = null;

        if (current[field] !== val) {
            const task = current.clone();
            const isTemplate = task instanceof TemplateTask;

            task[field] = val;

            // We updated using property notation, so we need to manually touch the task
            task.addedBy = getCurrentUserId();
            task.addedOn = newHRTimestamp();

            if (!isTemplate) startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, setHasChanged]);

    const onDurationChange = useCallback((duration: number) => {
        const task = current.clone() as Task;
        const isTemplate = task instanceof TemplateTask;

        task.duration = duration;

        // If a startTime is given, we need to move it back!
        if (task.startTime) task.dueDate = DateUtil.add(task.startTime, task.duration, 'hour');

        if (!isTemplate) startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Update the activity cost based on all the parameters.
     *
     * @param {Partial<Task>} toUpdate - {fieldName: value}
     * @param {Task} [taskOverride] - optional task to use, otherwise use state
     */
    const updateCost = useCallback((toUpdate: Partial<Task>, taskOverride?: Task): TaskLike => {
        const task = (taskOverride || current).clone();

        if (current instanceof TemplateTask) return Object.assign(task, toUpdate);
        else return TaskHelper.updateCost(task as Task, toUpdate);
    }, [current]);

    const onCostOverride = useCallback(() => {
        if (templateTask) {
            const _templateTask = templateTask.clone();
            _templateTask.isCostOverridden = !_templateTask.isCostOverridden;
            _templateTask.costOverride = _templateTask.isCostOverridden ? _templateTask.costOverride : 0
        }
        else if (task) {
            const _task = updateCost({
                isCostOverridden: !task.isCostOverridden,
                costOverride: task.isCostOverridden ? task.costOverride : 0
            });

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(_task);
        }
    }, [current, setHasChanged, task, templateTask, updateCost]);

    const onLaborRateOverride = useCallback(() => {
        if (!task) return;
        let _task = task.clone();

        _task = updateCost({
            isLaborRateOverridden: !_task.isLaborRateOverridden,
            laborRateOverride: _task.isLaborRateOverridden ? _task.laborRateOverride : 0
        }) as Task;

        startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(_task);
    }, [setHasChanged, task, updateCost]);

    const onTravelRateOverride = useCallback(() => {
        if (!task) return;
        let _task = task.clone();

        _task = updateCost({
            isTravelRateOverridden: !_task.isTravelRateOverridden,
            travelRateOverride: _task.isTravelRateOverridden ? _task.travelRateOverride : 0
        }) as Task;

        startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(_task);
    }, [setHasChanged, task, updateCost]);

    const onLaborTimeOverride = useCallback(() => {
        if (!task) return;
        let _task = task.clone();

        _task = updateCost({
            isLaborTimeOverridden: !task.isLaborTimeOverridden,
            laborTimeOverride: task.isLaborTimeOverridden ? _task.laborTimeOverride : 0
        }) as Task;

        startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(_task);
    }, [setHasChanged, task, updateCost]);

    const onTravelTimeOverride = useCallback(() => {
        if (!task) return;
        let _task = task.clone();

        _task = updateCost({
            isTravelTimeOverridden: !task.isTravelTimeOverridden,
            travelTimeOverride: task.isTravelTimeOverridden ? _task.travelTimeOverride : 0
        }) as Task;

        startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(_task);
    }, [setHasChanged, task, updateCost]);

    // Create the handler map with useMemo
    const overrideHandlers: OverrideHandlers = useMemo(() => ({
        laborRate: onLaborRateOverride,
        laborTime: onLaborTimeOverride,
        travelRate: onTravelRateOverride,
        travelTime: onTravelTimeOverride,
        cost: onCostOverride,
    }), [onCostOverride, onLaborRateOverride, onLaborTimeOverride, onTravelRateOverride, onTravelTimeOverride]);

    const laborRateRef = useRef(null);
    const laborTimeRef = useRef(null);
    const travelRateRef = useRef(null);
    const travelTimeRef = useRef(null);
    const costRef = useRef(null);

    const overrideRefs: OverrideRefs = useMemo(() => ({
        laborRate: laborRateRef,
        laborTime: laborTimeRef,
        travelRate: travelRateRef,
        travelTime: travelTimeRef,
        cost: costRef,
    }), []);

    const onNoCharge = useCallback(() => {
        const isTemplate = current instanceof TemplateTask;

        if (isTemplate) {
            const task = current.clone();
            task.noCharge = !task.noCharge;
            setHasChanged(true);
            setCurrent(task);
        }
        else {
            const task = updateCost({'noCharge': !current.noCharge});

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, setHasChanged, updateCost]);

    const onHideFromInvoice = useCallback(() => {
        const isTemplate = current instanceof TemplateTask;

        if (isTemplate) {
            const task = current.clone();
            task.hideFromInvoice = !task.hideFromInvoice;
            setHasChanged(true);
            setCurrent(task);
        }
        else {
            const task = updateCost({'hideFromInvoice': !current.hideFromInvoice});

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, setHasChanged, updateCost]);

    const onDateChange = useCallback((date: Date) => {
        const task = current.clone() as Task;

        task.dueDate = date;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    const onStartTimeChange = useCallback((date: Date) => {
        const task = current.clone() as Task;
        task.startTime = date;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Called when a task is marked completed or not completed in the TaskTimer.
     *
     * @param {SyntheticEvent} e
     * @param {any} [_data]
     */
    const onCompletionToggled = useCallback((e: SyntheticEvent, _data?: any) => {
        e.preventDefault();

        const task = current as Task;

        const taskClone = handleCheckboxClick(task);

        startEditingPageF('task', taskClone.id);

        setHasChanged(true);
        setCurrent(taskClone);
    }, [current, setHasChanged]);

    /**
     * Called when a date range is selected in the Custom (calendar) time filter.
     *
     * If a null is received, it means the user cleared the search. In that case we want to move to All view
     *
     * @param {Date} date - start and end dates in an array, or null to clear
     */
    const onChangeCompletionDate = useCallback((date: Date) => {
        const task = current.clone() as Task;
        task.completionDate = date;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Called when a task is marked private
     * @param e
     */
    const onPrivateClick = useCallback((e: SyntheticEvent) => {
        e.preventDefault();

        const task = current.clone() as Task;
        task.private = !task.private;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Called when a task is marked private
     * @param e
     */
    const onGlobalClick = useCallback((e: SyntheticEvent) => {
        e.preventDefault();

        const task = current.clone() as TemplateTask;
        task.global = !task.global;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Remove all tieIns for the given area.
     *
     * @param {string} module -, e.g. 'Marketing'
     * @param {string} entity -, e.g. 'Lead'
     * @param {Task} task
     * @return {Task}
     * @private
     */
    const _removeTieIns = useCallback((module: string, entity: string, task: Task): Task => {
        task.tieIns = (task.tieIns ?? []).filter(ti => !(ti.module === module && ti.entity === entity));

        return task;
    }, []);

    /**
     * Get a list of campaigns, both permanent and temporary
     */
    const taskCampaigns = useMemo(() => {
        let campaigns: Campaign[] = props.campaigns.filter(c => c.status !== 'D');

        if (unsavedEntity instanceof Campaign) {
            campaigns.push(unsavedEntity);
        }

        return campaigns.sort((a, b) => a.name.localeCompare(b.name));
    }, [props.campaigns, unsavedEntity]);

    const marketingTieIns = useMemo(() => {
        return tieIns
            .filter(ti => ti.module === 'Marketing' && ti.entity === 'Campaign')
            .filter(ti => taskCampaigns.find(c => c.id === ti.id)) // Filter out any deleted campaigns
            .map(ti => {
                // Get the matching campaign
                const campaign = taskCampaigns.find(c => c.id === ti.id)!;

                return {
                    label: campaign.name,
                    value: campaign.name,
                    campaign
                };
            });
    }, [taskCampaigns, tieIns]);

    /**
     * Look for related areas, like Sales, Marketing, Human Resources and Analytics
     */
    const relatedAreas = useMemo(() => {
        const relatedAreas = new Set<string>();

        getCategoriesForUser().forEach(c => {
            if (current.categories.includes(c.id)) {
                c.relatedAreas.forEach(cat => relatedAreas.add(cat));
            }
        });

        return Array.from(relatedAreas.values());
    }, [current.categories, getCategoriesForUser]);

    /**
     * Get a list of leads.
     * Be smart: if this task is not invoiced, do not show leads that have converted out (as we likely only care
     * about open leads).
     */
    const taskLeads = useMemo(() => {
        if (!task) return [];

        let leads: Lead[] = props.leads.filter(l => l.status !== 'D').filter(util.existsFilter);
        if (!task.invoiceId && !task.tieIns?.some(ti => ti.module === 'Sales')) {
            leads = leads.filter(l => {
                const ms = LeadHelper.getLatestMilestone(l);
                if (ms) return !MilestoneHelper.isWonOrLater(ms);
                else return true;
            });
        }

        if (unsavedEntity instanceof Lead) {
            if (!leads.find(l => l.id === unsavedEntity.id)) leads.push(unsavedEntity);
        }

        return leads;
    }, [unsavedEntity, props.leads, task]);

    const salesTieIns = useMemo(() => {
        return tieIns
            .filter(ti => ti.module === 'Sales' && ti.entity === 'Lead')
            .filter(ti => taskLeads.find(c => c.id === ti.id)) // Filter out any deleted leads
            .map(ti => {
                // Get the matching campaign
                const lead = taskLeads.find(c => c.id === ti.id)!;

                return {
                    label: lead.name,
                    value: lead.name,
                    lead
                };
            });
    }, [taskLeads, tieIns]);

    /**
     * Remove tieIns that are no longer needed.
     *
     * @param {Task} task
     * @return {Task}
     * @private
     */
    const _purgeRelatedAreas = useCallback((task: Task): Task => {
        // Purge no longer needed tieIns
        if (!relatedAreas.includes('Marketing') && marketingTieIns.length > 0) {
            task = _removeTieIns('Marketing', 'Campaign', task);
        }
        if (!relatedAreas.includes('Sales') && salesTieIns.length > 0) {
            task = _removeTieIns('Sales', 'Lead', task);
        }
        if (!relatedAreas.includes('Client') && matterTieIns.length > 0) {
            task = _removeTieIns('Client', 'Matter', task);
        }

        return task;
    }, [_removeTieIns, marketingTieIns, relatedAreas, matterTieIns.length, salesTieIns.length]);

    /**
     * Called when the selected categories changes.
     *
     * If a category has a relatedArea, need to remove any tieIns for that area.
     *
     * @param {string[]} categories - an array of string values or empty array
     */
    const onCategoryChange = useCallback((categories: string[]) => {
        // Need to map category names to category objects
        const selectedCategories = getCategoriesForUser()
            .filter(c => !!categories.find(cs => cs === c.name));

        let task = current.clone();
        const isTemplate = task instanceof TemplateTask;
        const _task = task instanceof Task ? task : null;

        // If this is HR related, and we are an owner or have the HR role, make it private
        const isHR = selectedCategories.some(sc => sc.relatedAreas.includes('Human Resources'));
        const hasPermissions = Auth.isOwner() || Auth.hasRole(getCurrentUserId(), 'Human Resources');
        if (isHR && hasPermissions && _task) _task.private = true;

        if (_task) task = _purgeRelatedAreas(_task);

        // If this is client-related, make the task billable
        task.billable = selectedCategories.filter(sc => sc.relatedAreas.includes('Client')).length > 0;

        task.categories = selectedCategories.map(sc => sc.categoryId);
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        if (!isTemplate) startEditingPageF('task', current.id);

        if (!hasChanged) setHasChanged(true);

        setCurrent(task);

    }, [_purgeRelatedAreas, current, getCategoriesForUser, hasChanged, setCurrent, setHasChanged]);

    /**
     * Called when a task is reassigned to another user.
     */
    const onReassign = useCallback((item: { data: User | Group }) => {
        if (task && hasTaskTimerStarted(task)) return;

        const data = item.data;

        if (data.id !== current.assignedTo) {
            let task = current.clone();

            const _task = task instanceof Task ? task : null;

            task.assignedToType = data instanceof User ? AssignedToType.User : AssignedToType.Group;
            task.assignedTo = data.id;
            if (_task && _task.notification?.status) {
                _task.notification.to = data.id;
            }
            task.addedBy = getCurrentUserId();
            task.addedOn = newHRTimestamp();

            const billableRate = getBillableRate(task);

            // Change the billable rates for this user.
            if (_task) {
                // Update the labor rate if not already overridden
                if (!_task.isLaborRateOverridden)
                    _task.laborRate = billableRate;

                // Update the travel rate if not already overridden
                if (!_task.isTravelRateOverridden)
                    _task.travelRate = billableRate;

                // Always update the associated rate with that for the case
                _task.associatedRate = billableRate;

                task = TaskHelper.updateCost(_task, undefined, true);

                startEditingPageF('task', _task.id);
            }

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, getBillableRate, setHasChanged, task]);

    const onFlatFeeBucketChange = useCallback((value: SelectOption<string, { flatFee?: FlatFee, milestone?: Milestone }>) => {
        const task = current.clone() as Task;

        task.flatFeeBucket = value?.data?.flatFee?.id ?? null;
        task.flatFeeMilestoneBucket = value?.data?.milestone?.id ?? null;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    const onDocketChange = useCallback((docketId: uuid) => {
        const task = current.clone() as Task;
        task.addedBy = getCurrentUserId()
        task.addedOn = newHRTimestamp();
        task.docketId = docketId;

        if (!docketId) task.roomNumber = null;
        else task.roomNumber = task.roomNumber ?? props.dockets.find(d => d.id === docketId)?.roomNumber;

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, props.dockets, setHasChanged]);

    const onMarketingCampaign = useCallback((campaigns: ({ campaign: Campaign })[]) => {
        const task = current.clone() as Task;

        // Replace tieIns with "Marketing" "Campaign" with those in campaigns
        const newTieIns = campaigns.map(c => {
            return {
                module: 'Marketing',
                entity: 'Campaign',
                id: c.campaign.id
            };
        });

        task.tieIns = (task.tieIns ?? [])
            .filter(ti => !(ti.module === 'Marketing' && ti.entity === 'Campaign'))
            .concat(newTieIns);

        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    const onSalesLead = useCallback((leads: ({ data: Lead })[]) => {
        const task = current.clone() as Task;

        // Replace tieIns with "Sales" "Lead" with those in leads
        const newTieIns = leads.map(l => {
            return {
                module: 'Sales',
                entity: 'Lead',
                id: l.data.id
            };
        });

        // OWA-829: a billable entry belongs to exactly one billing container. Choosing a lead
        // also drops any case tie-in, otherwise the entry is eligible on both a lead invoice
        // and a case invoice.
        _removeTieIns('Sales', 'Lead', task);
        if (newTieIns.length > 0) _removeTieIns('Client', 'Matter', task);
        task.tieIns = (task.tieIns ?? []).concat(newTieIns);

        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [_removeTieIns, current, setHasChanged]);

    /**
     * Only one matter allowed.  Matters is an object with label, value, matter
     * @param {Matter} matter
     */
    const onMatterChanged = useCallback((matter: Matter) => {
        // matter can be null!  If so, we blank out the matter.
        const task = current.clone() as Task;
        const oldMatterId = task.tieIns?.find(ti => ti.module === 'Client' && ti.entity === 'Matter')?.id;

        if (!matter) {
            task.tieIns = (task.tieIns ?? []).filter(ti => !(ti.module === 'Client' && ti.entity === 'Matter'));
            task.addedBy = getCurrentUserId();
            task.addedOn = newHRTimestamp();

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
        else if (matter.id !== oldMatterId) {
            // Replace tieIns with "Client" "Matter" with those in matters
            const newTieIn = {
                module: 'Client',
                entity: 'Matter',
                id: matter.id
            };

            // OWA-829: choosing a case also drops any lead tie-in (see onSalesLead).
            _removeTieIns('Client', 'Matter', task);
            _removeTieIns('Sales', 'Lead', task);
            const billableRate = getBillableRate(task, matter);

            task.tieIns = (task.tieIns ?? []).concat(newTieIn);
            task.laborRate = billableRate;
            task.travelRate = billableRate;
            task.addedBy = getCurrentUserId();
            task.addedOn = newHRTimestamp();

            // If this is a flat fee case (only), auto-populate the flat fee bucket
            if (matter.rateType === 'f') {
                const matterFlatFees = props.flatFees.getMappingFor('matterId', matter.id);
                // Prefer an explicit default; fall back to the sole bucket if only one exists
                const def = matterFlatFees?.find(ff => ff.isDefault)
                            || (matterFlatFees?.length === 1 ? matterFlatFees[0] : undefined);
                if (def) task.flatFeeBucket = def.id;
            }

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
    }, [_removeTieIns, current, getBillableRate, props.flatFees, setHasChanged]);

    /**
     * Called when a contact is associated to a task.
     *
     * RelatedTo is an array, so we are using a multi-input select
     *
     * @param {uuid[]} contacts - the ids of the selected Contacts
     */
    const onRelatedTo = useCallback((contacts: uuid[]) => {
        // Update the task with the new contacts
        const task = current.clone() as Task;
        task.relatedTo = contacts;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        // Update state with removed contacts (if in state and not in contacts, remove from state)
        // const _contacts = contacts.filter(c => contacts.map(c => c.contact.id).includes(c.id));

        startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Called when the whole form has been submitted, using one of the checkmark "OK" buttons.
     *
     * @param e
     */
    const onOkClick = useCallback(async (e?: SyntheticEvent) => {
        e?.preventDefault();

        // Refuse to save (and thus close) the task while a document editor is
        // still open inside it. Saving the task tears down TaskItem and runs
        // uncacheDependents() on the autosave cache, which would silently wipe
        // the open editor's draft. Make the user OK or cancel the editor first.
        if (subEditActive > 0) {
            confirmWarning(
                'Document still open',
                'Please save or close the document you have open in this task before saving the task.',
                'OK',
                () => {}
            );
            return;
        }

        const quickAction = props.quickAction;

        const _doSubmit = async (_task?: TaskLike) => {
            const task: TaskLike = _task || current.clone();
            const isTemplate = task instanceof TemplateTask;

            if (task.isValid()) {
                const userOrGroup = TaskHelper.getAssigned(task);

                task.addedBy = getCurrentUserId();
                task.addedOn = newHRTimestamp();

                if (task instanceof Task) task.associatedRate = userOrGroup?.billableRate ?? 0; // OWA-554: no assigned user/group → no rate (0), as consumed downstream (... || associatedRate || 0)

                if (!isTemplate) {
                    clientsRef?.current?.saveNew();
                    stopEditingPageF('task', current.id);

                    // Call autosave with saving flag

                    // If we are in associatedTasks, there will be a props.env.topLevelId. Otherwise we are in the grid, and
                    // we can mark the parentId as null
                    task.waitingToSave = true;

                    // Call autosave
                    callStorageWorker(task);
                }

                doResetRef.current = true;

                props.onOk(task);

                stopEditingPageF('task', task.id);

                if (!isTemplate) {
                    // Cancel the pending debounced autosave so it can't fire after cleanup
                    // and resurrect the entry. The saga (taskSagas saveTask) is the one
                    // responsible for canonical state from here.
                    clearStorageWorker();

                    // If we are in associatedTasks, there will be a props.env.topLevelId.
                    // Otherwise we are in the grid, and we can mark the parentId as null.
                    // Use the same parentId the writer used so uncacheItem matches.
                    const parentId = props.env?.topLevelId ?? getZeroUUID();

                    // Fire-and-forget — there's no reason to block dialog close on IDB cleanup.
                    // OWA-958: remove only this editor's own draft. Unsaved documents typed
                    // inside the task are drafts of their own, and the save saga no longer
                    // removes any draft.
                    void window.storageWorker
                        .uncacheItem(getCurrentFirmId(), getCurrentUserId(), 'task', parentId, current.id)
                        .catch((err: any) => console.error(err));
                }
            }
            else {
                Alert.addSimpleError(task.validationError ?? '');
            }
        };

        const _notifyCompletion = () => {
            // Need to signal that the task has been saved, and that the new task should be loaded
            dispatch(MiscActions.showTask(quickAction.taskToShow));
        };

        if (quickAction && quickAction.taskToShow && !quickAction.oldTaskSaved) {
            if (hasChanged) {
                // Ask if they want to save the current one
                confirmWarningOkCancel(
                    'Save Activity?',
                    (
                        <div>
                            <p>A new activity has been started, shall we save this one before proceeding?</p>
                        </div>
                    ),
                    'Yes', 'No',
                    async () => {
                        const task = stopTimer(current as Task, getCurrentUserId());

                        if (task.isValid()) await _doSubmit();
                        else {
                            task.title = 'Interrupted task';

                            await _doSubmit(task);
                        }

                        _notifyCompletion();
                    },
                    () => {
                        _notifyCompletion();
                    }
                );
            }
            else _notifyCompletion();
        }
        else await _doSubmit();
    }, [callStorageWorker, clearStorageWorker, clientsRef, current, dispatch, hasChanged, props, subEditActive]);

    // Ctrl+S / Cmd+S to save
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (hasChanged) onOkClick();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasChanged, onOkClick]);

    const onCancel = useCallback(async (e: SyntheticEvent) => {
        e.preventDefault();

        // Refuse to close while a document editor is open inside this task.
        // Without this guard, _doWork below calls uncacheDependents() which wipes
        // the unsaved draft from the autosave IDB cache before the user has had
        // a chance to OK the editor. The note would then be lost with no warning
        // because the draft was never dispatched to the saveDocumentQueue saga.
        if (subEditActive > 0) {
            confirmWarning(
                'Document still open',
                'Please save or close the document you have open in this task before closing the task.',
                'OK',
                () => {}
            );
            return;
        }

        const isTemplate = current instanceof TemplateTask;

        const _doWork = async () => {
            if (!isTemplate) {
                stopEditingPageF('task', current.id);

                try {
                    clientsRef?.current?.deleteNew();
                }
                catch (err) {
                    Alert.add(new Alert(Alert.ERROR, current.id, current.addedOn, err.message));
                }
            }

            doResetRef.current = true;

            // Handle deletion of temporary comments if this is a new entry
            if (isNew) {
                const comments = props.comments.getMappingFor('entityId', current.id);
                comments.forEach(c => deleteComment(c));
            }

            // TODO: If this task was created and already running, we need to delete it.
            // Only discard a genuinely abandoned new task. A duplicated task stays
            // isNew/fresh even after autosave persists it; deleting a persisted task
            // here silently destroys real, edited data (OWA: Lawson Legal task loss).
            const existingTask = props.tasks.get(current.id);
            if (isNew && !!existingTask && !existingTask.persisted && !existingTask.dbAddedOn) {
                deleteTask(existingTask);
            }

            hasCanceledRef.current = true;

            props.onCancel();

            if (!isTemplate) {
                // Cancel any pending debounced autosave so it can't fire post-cleanup.
                clearStorageWorker();

                // Use the same parentId the writer used so uncacheItem matches.
                const parentId = props.env?.topLevelId ?? getZeroUUID();

                // Fire-and-forget — no reason to block dialog close on IDB cleanup.
                void window.storageWorker
                    .uncacheDependents(getCurrentFirmId(), getCurrentUserId(), 'task', parentId, current.id)
                    .catch((err: any) => console.error(err));
                void window.storageWorker
                    .uncacheItem(getCurrentFirmId(), getCurrentUserId(), 'task', parentId, current.id)
                    .catch((err: any) => console.error(err));
            }
        };

        if (hasChanged && !props.noCloseWarning) {
            // Ask if they really want to save
            confirmChangesMade(_doWork);
        }
        else {
            await _doWork();
        }
    }, [clearStorageWorker, clientsRef, current, hasChanged, isNew, props, subEditActive]);

    const onDeleteTask = useCallback((e: SyntheticEvent) => {
        e.preventDefault();

        if (!task) return;

        let {canDelete, message} = util.getDeleteImpact('task', task, deleteImpactTask);
        if (canDelete && !message) message = <div>
            <p>About to delete <strong>{task.title}</strong></p>
            <p>Proceed?</p>
        </div>;

        confirmWarningOkCancel(
            'Delete Task',
            message ?? '',
            'Yes', 'No',
            async () => {
                hasCanceledRef.current = true;
                doResetRef.current = true;
                stopEditingPageF('task', task.id);

                deleteTask(task);
                props.onCancel();

                // Cancel any pending debounced autosave so it can't fire post-cleanup.
                clearStorageWorker();

                // Clear cached task from storage worker so TaskGrid doesn't re-open it.
                // Fire-and-forget — no reason to block dialog close on IDB cleanup.
                const parentId = props.env?.topLevelId ?? getZeroUUID();
                void window.storageWorker
                    .uncacheDependents(getCurrentFirmId(), getCurrentUserId(), 'task', parentId, task.id)
                    .catch((err: any) => console.error(err));
                void window.storageWorker
                    .uncacheItem(getCurrentFirmId(), getCurrentUserId(), 'task', parentId, task.id)
                    .catch((err: any) => console.error(err));
            },
            () => {}
        );
    }, [task, props, clearStorageWorker]);

    /**
     * Categories array is a list of category objects, not strings.
     * We need to pass a list of strings to MultiSelect.
     */
    const getCategoryNames = useCallback(() => {
        const categories = getCategoriesForUser() || [];

        return categories.map(c => c.name);
    }, [getCategoriesForUser]);

    const categoryHints = useMemo(() => {
        const categories = getCategoriesForUser() || [];

        return categories.map(c => {
            // if (c.description || (c.relatedAreas && c.relatedAreas.length > 0)) return (
            //     <div className='task-item__category'>
            //         <span className='task-item__category-description'>{c.description}</span>
            //         {(c.relatedAreas && c.relatedAreas.length > 0) && c.relatedAreas.map(r => <SystemLabel key={r} name={r}/>)}
            //     </div>
            // );
            if (c.description || (c.relatedAreas && c.relatedAreas.length > 0)) {
                const relatedAreas = c.relatedAreas && c.relatedAreas.length > 0
                                     ? c.relatedAreas.map(c => c.toUpperCase()).join(', ')
                                     : '';
                return `${c.description || ''} [${relatedAreas}]`.trim();
            }
            else
                return '';
        });
    }, [getCategoriesForUser]);

    // Task.categories is an array uuids, not strings.
    // We need to pass a list of strings to MultiSelect
    const selectedCategoryNames = useMemo(() => {
        // categories is Category[]
        // task.categories is uuid[]

        // If there are no categories, nothing to worry about
        if (!Array.isArray(getCategoriesForUser()) || !Array.isArray(current.categories)) return [];

        return getCategoriesForUser()
            .filter(c => current.categories.includes(c.id))
            .map(c => c.name);
    }, [current.categories, getCategoriesForUser]);

    /**
     * Return a list of options for the Campaigns link.
     *
     * @returns {Array.<{label: ReactElement, value: string, campaign: Campaign}>}
     */
    const marketingCampaigns = useMemo((): SelectOption<string, Campaign>[] => {
        return taskCampaigns
            .map(campaign => {
                return {
                    label: <IconButton type={CampaignHelper.getIcon(campaign) ?? Icons.MARKETING}
                                       svgWidth={1.2}
                                       label={campaign.name}
                    />,
                    value: campaign.name,
                    campaign
                };
            });
    }, [taskCampaigns]);

    /**
     * Return a list of options for the Leads link.
     */
    const getSalesLeads = useCallback((task?: Task) => {
        const leads = LeadHelper.renderLeads(taskLeads);

        if (task) {
            // Only return leads that this task's tieIns reference
            const salesTieIns = (task.tieIns ?? [])
                .filter(ti => ti.module === 'Sales' && ti.entity === 'Lead')
                .filter(ti => taskLeads.find(l => l.id === ti.id))
                .map(ti => ti.id);

            return leads.filter(l => salesTieIns.includes(l.data?.id ?? ''));
        }
        else return leads;
    }, [taskLeads]);

    /**
     * Called when there is a change in a duration field.
     *
     * @param {string} field
     * @param {number} value
     */
    const handleDurationChange = useCallback((field: string, value: number) => {
        const isTemplate = current instanceof TemplateTask;

        if (isTemplate) {
            const task = current.clone();
            task.duration = Math.min(0, value);

            setHasChanged(true);
            setCurrent(task);
        }
        else {
            let task = current as Task;

            const obj = {};
            let duration = value;
            if (duration === -1) duration = 0;

            // If entering travel time, it can't be more than the (overridden) total time
            if (field === 'travelTime' || field === 'travelTimeOverride') {
                const invoice = task.invoiceId ? InvoiceHelper.getInvoiceFromId(task.invoiceId) : null;

                const billingIncrement = invoice?.billingIncrement ?? optionHelper.getValue(OptionNames.BILLING_INCREMENT);
                const roundingMethod = invoice?.roundingMethod ?? optionHelper.getValue(OptionNames.ROUNDING_METHOD);

                const elapsed = roundDurationToMinutes(task.elapsedTime, billingIncrement, roundingMethod);

                let maxTime = task.isLaborTimeOverridden ? elapsed - task.laborTimeOverride : elapsed;
                if (isNaN(maxTime)) maxTime = 0;
                duration = Math.min(duration, maxTime);
            }

            obj[field] = duration;

            task = updateCost(obj) as Task;

            startEditingPageF('task', current.id);

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, optionHelper, setHasChanged, updateCost]);

    // OWA-perf: Diagnostic-only onChange for the invoiceNotes textarea. The field is uncontrolled
    // (defaultValue) so the DOM displays typed text without React. The actual commit to Task state
    // happens in onInvoiceNotesBlur. This handler only measures per-keystroke processing time and
    // logs a single warn line when something on the path (extension hook, autosave write, layout)
    // pushes a keystroke past 250ms — to help diagnose the sporadic 30s lag report we can't repro.
    const onInvoiceNotesChange = useCallback((e: SyntheticEvent | null, _: string | null, value: string) => {
        const start = performance.now();
        // Defer measurement to next frame so we capture the full input → paint cost, not just the JS callback.
        requestAnimationFrame(() => {
            const processingMs = performance.now() - start;
            if (processingMs <= 250) return;

            const target = e?.target as HTMLTextAreaElement | null;
            const wrapper = target?.closest('.task-item__invoice-notes') ?? target?.parentElement;
            // Look for known password-manager / extension injection attributes near the field.
            const extensionAttrs: string[] = [];
            if (wrapper) {
                const scan = (el: Element) => {
                    for (const attr of el.attributes) {
                        const name = attr.name;
                        if (name.startsWith('data-lastpass') || name.startsWith('data-1p')
                            || name.startsWith('data-bw') || name.startsWith('data-dashlane')
                            || name.startsWith('data-lpignore') || name === 'data-form-type') {
                            extensionAttrs.push(`${el.tagName.toLowerCase()}[${name}]`);
                        }
                    }
                };
                scan(wrapper);
                wrapper.querySelectorAll('*').forEach(scan);
            }

            // Cheap size estimate; only fires on the slow path so we don't pay the cost in the common case.
            let autosaveSize = 0;
            try { autosaveSize = JSON.stringify(current).length; } catch { /* circular — ignore */ }

            console.warn('[invoiceNotes-lag]', {
                processingMs: Math.round(processingMs),
                valueLength: (value || '').length,
                taskId: current?.id,
                extensionAttrs,
                injectedSiblings: wrapper ? wrapper.children.length : 0,
                autosaveSize,
                propsCurrentChurn: propsCurrentChurnRef.current,
            });
        });
    }, [current]);

    /**
     * Update current with changed field value.
     *
     * @param {ChangeEvent<HTMLInputElement>} e
     * @param {string} field - The name of the field
     * @param {string} [override] - Override the value in e.target.value
     */
    const handleTextChange = useCallback((e: ChangeEvent<HTMLInputElement>, field: string, override?: string) => {
        e.preventDefault();

        const isTemplate = current instanceof TemplateTask;

        if (field === 'invoiceNotes') onInvoiceNotesChange(null, null, e.target.value);
        else {
            const obj = {};
            obj[field] = override || e.target.value;

            const task = updateCost(obj);

            if (!isTemplate) startEditingPageF('task', current.id);

            const _fromTemplate = (field === 'title' && (current instanceof Task) && !(override || e.target.value))
                                  ? false
                                  : fromTemplate;

            const _manualTitle = (field === 'title' && (current instanceof Task) && ((e.target.value !== '') || !!(override || e.target.value)));

            setHasChanged(true);
            setCurrent(task);
            setFromTemplate(_fromTemplate);
            setManualTitle(_manualTitle);
        }
    }, [current, fromTemplate, onInvoiceNotesChange, setHasChanged, updateCost]);

    /**
     * The edit state of the Task Timer has changed.
     *
     * @param {boolean} value
     */
    const onTimerStateChange = useCallback((value: boolean) => {
        if (typeof (value) !== 'boolean') console.warn('expecting boolean in onTimerStateChange');
        setIsEditingTimer(value);
    }, [setIsEditingTimer]);

    /**
     * Get IconButtons for the task template.
     *
     * @param {TemplateTask} _taskTemplate
     * @private
     */
    const _getIconsForTaskTemplate = useCallback((_taskTemplate: TemplateTask) => {
        // Get the related category and match up with a symbol
        return _taskTemplate.categories.map((cid) => {
            let pos = 0;
            let icons: ReactElement[] = [];

            // These will be names, like Sales and Administrative.  Personal categories will 'Personal' as their related area
            let categoryNames = props.categories.get(cid)?.relatedAreas ?? [];

            // Get IconButton labels for each of the category names
            const iconLabels = categoryNames.map(cn => props.activityCategories.find(c => c.relatedArea === cn)?.icon)
                .filter(util.existsFilter);

            icons.push(...iconLabels.map(label => {
                const color = label === 'USER' ? 'navy' : undefined
                return <IconButton key={`tt-ico-${_taskTemplate.id}-${pos++}`} type={Icons[label]}
                                   label={_taskTemplate.title} color={color}/>
            }));

            if (_taskTemplate.favoritedBy.includes(getCurrentUserId())) icons.push(
                <IconButton key={`tt-ico-${_taskTemplate.id}-${pos++}`} type={Icons.STAR_FILLED}/>
            );

            return icons;
        }).filter(util.existsFilter);
    }, [props.activityCategories, props.categories]);

    /**
     * When the user types into the title field on a new task, show available task templates below.
     *
     * @param {TemplateTask} [template]
     */
    const getTemplates = useCallback((template?: TemplateTask) => {
        const {title} = current;

        const templates = props.templateTasks.filter(t => t.status !== 'D').filter(t => title ? t.title.toLowerCase().includes(title?.toLowerCase()) : true)
            .sort((a, b) => {
                const aFav = a.favoritedBy.includes(getCurrentUserId());
                const bFav = b.favoritedBy.includes(getCurrentUserId());

                if (aFav && !bFav) return -1;
                else if (!aFav && bFav) return 1;
                else return a.title.localeCompare(b.title);
            })

        const results = templates.map(t => {
            const icons = _getIconsForTaskTemplate(t);

            return {
                label: <div key={`tt-lbl-${t.id}`} className='u-row'>{...icons}</div>,
                value: t.title,
                data: t
            };
        });

        if (template) return results.filter(o => template.title === o.value);
        else return results;
    }, [_getIconsForTaskTemplate, current, props.templateTasks]);

    /**
     * Called when text changes in the title's select field. Indicates the user is searching for a template.
     * If blur has already been called, we drop out.
     * If the fromTemplate state is false, we drop out.
     *
     * We just set the new title replace the task in state.
     *
     * @param {string} title - the new text in the select field
     * @param {InputActionMeta} _
     */
    const onTemplateTextChange = useCallback((title: string, _: InputActionMeta) => {
        // Don't call if we have already chosen a template.
        // This issue is that after an onBlur, this gets called TWICE with an empty value!
        if (!blurCalled && !fromTemplate) {
            const taskClone = current.clone();
            taskClone.title = title;
        }
    }, [blurCalled, current, fromTemplate]);

    /**
     * The user changed the title text and now it is empty
     */
    const onEmptyInput = useCallback(() => {
        setFromTemplate(false);
        setManualTitle(false);
    }, []);

    /**
     * Called when the user leaves the template field.
     * If the title is blank, or is [Awaiting Title], drop out.
     *
     * We set the hasChanged and fromTemplate fields in the state.
     * After saving the state, we set the blurCalled field back to false (it's basically a mutex).
     */
    const onTemplateBlur = useCallback((e) => {
        // If there is anything in title, save it
        if (current?.title && current.title !== AWAITING_TITLE) {
            // TODO: Does the call to setHasChanged need to be a callback from setBlurCalled?
            setBlurCalled(true);
            setHasChanged(true, () => setBlurCalled(false));
        }
    }, [current, setHasChanged]);

    const onTemplateCreate = useCallback((value: string) => {
        const taskClone = current.clone();
        taskClone.title = value;
        setCurrent(taskClone);

        setHasChanged(true);
        setFromTemplate(false);
        setManualTitle(true);

        // Move to the next field, description.
        setTimeout(() => descriptionRef.current?.focus(), 10);
    }, [current, setHasChanged]);

    /**
     * Apply a template when selected from the Select.
     *
     * The itemStruct provides three pieces of information:
     * - label is a string value with the title of the template
     * - value is the new title for the task
     * - data is the template itself
     *
     * If the user selected a template, itemStruct.data will be an object.
     * If itemStruct is null, the field was cleared.
     * Otherwise, it was just text for a new task, no template selected.
     *
     * @param {SelectOption<string, TemplateTask>} itemStruct
     */
    const onTemplateChange = useCallback((itemStruct?: SelectOption<string, TemplateTask>) => {
        if (itemStruct?.data instanceof TemplateTask) {
            if (!task) return;
            // Only the attributes we care about pass over.
            const {
                id,
                duration,
                elapsedTime,
                lastStart,
                running,
                tieIns,
                assignedTo,
                assignedToType,
                relatedTo,
                startTime,
                invoiceNotes
            } = task;

            // Now create a new task, starting with anything already entered (especially the id!), then the template data.
            const taskClone = new Task({
                duration,
                elapsedTime,
                lastStart,
                running,
                tieIns,
                assignedTo,
                assignedToType,
                relatedTo,
                startTime,
                private: task.private,
                invoiceNotes,
                // cast to object so the intentional template override doesn't trip
                // TS2783 (assignedToType is also listed explicitly above)
                ...(itemStruct.data as object),
                dueDate: task.dueDate ?? DateUtil.endOfDay(new Date(), true),
                fromTemplateId: itemStruct.data.id,
                taskId: id
            });

            setHasChanged(true);
            setCurrent(taskClone);
            setFromTemplate(true);
            setManualTitle(false);
        }
        else if (itemStruct) {
            // User entered something not linked to a template
            const taskClone = current.clone();
            taskClone.title = itemStruct.value;

            setHasChanged(true);
            setCurrent(taskClone);
            setFromTemplate(false);
            setManualTitle(true);
        }
        else {
            // Clear button pressed
            const taskClone = current.clone();
            taskClone.title = '';

            setHasChanged(true);
            setCurrent(taskClone);
            setFromTemplate(false);
            setManualTitle(false);
        }
    }, [current, setHasChanged, task]);

    /**
     * Task timer has returned a new value (manually entered).
     *
     * @param {number} value
     */
    const onAcceptTimerValue = useCallback((value: number) => {
        const task = current.clone() as Task;

        // Group-assigned and unassigned tasks are claimed consistently with startTimer (OWA-336).
        if (!hasTaskTimerStarted(task) && canClaimTaskForTimerWork(task)) {
            task.assignedTo = getCurrentUserId();
            task.assignedToType = AssignedToType.User;
        }

        // If there was no time for this entry before, set the current rate
        if (task.elapsedTime === 0) task.laborRate = getBillableRate(task, TaskHelper.getRelatedMatters(task)[0]);

        // Parse the timer entry
        task.elapsedTime = value;

        // TODO: Technically this is done automatically when the task changes, is this duplicate behavior?
        const updatedTask = updateCost(task);

        setHasChanged(true);
        setCurrent(updatedTask);
        setIsEditingTimer(false);
    }, [current, getBillableRate, setHasChanged, updateCost]);

    /**
     * Called when the billable ch
     */
    const onBillable = useCallback(() => {
        const task = current.clone();

        const _task = task instanceof Task ? task : null;

        // We calculate the billable rate only when required, in this case when a task is marked as billable
        const rate = getBillableRate(task);

        if (_task) {
            _task.laborRate = rate;
            _task.travelRate = rate;
            _task.associatedRate = rate;

            startEditingPageF('task', current.id);
        }

        task.billable = !task.billable;

        setHasChanged(true);
        setCurrent(task);
    }, [current, getBillableRate, setHasChanged]);

    const onFavoriteClick: CheckboxClickEvent = useCallback((e: SyntheticEvent) => {
        e.preventDefault();

        const task = current.clone();
        const isTemplate = task instanceof TemplateTask;

        if (isTemplate) {
            if (task.favoritedBy.includes(getCurrentUserId())) task.removeFavoritedBy(getCurrentUserId());
            else task.addFavoritedBy(getCurrentUserId());

            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, setHasChanged]);

    /**
     * Called when the Task Timer starts.
     *
     * @param {Event} e
     */
    const onStartTimer = useCallback((e: SyntheticEvent) => {
            e.preventDefault();

            const taskClone = current.clone() as Task;
            const _fromTemplate = taskClone.fromTemplateId != null;

            // Inside of TaskItem, we need to be careful not to trigger the logic in useState that
            // automatically stops timers when an external change is detected
            // (usually from an Audit Log action).
            setTimerChange(true, () => {
                const task = startTimer(taskClone, getCurrentUserId());

                // Now we can call
                if (!hasChanged) setHasChanged(true);
                if (fromTemplate !== _fromTemplate) setFromTemplate(_fromTemplate);
                setCurrent(task);
                setTimerChange(false);
            })
        },
        [current, setHasChanged, setTimerChange, fromTemplate, hasChanged]);

    /**
     * Called when the Task Timer is paused.
     *
     * @param {Event} e
     */
    const onPauseTimer = useCallback((e: SyntheticEvent) => {
            e.preventDefault();

            let task = current.clone() as Task;
            task = stopTimer(task, getCurrentUserId());

            setHasChanged(true);
            setCurrent(task);
        },
        [current, setHasChanged]);

    /**
     * Get matters related to this task.
     */
    const taskMatters = useMemo((): Matter[] => {
        return tieIns
            .filter(ti => ti.module === 'Client' && ti.entity === 'Matter') // get client/matter tieIns
            .filter(ti => !!props.matters.get(ti.id) || unsavedEntity?.id === ti.id) // filter to matters that exist
            .map(ti => {
                // Get the matching matter
                return props.matters.get(ti.id) || (unsavedEntity?.id === ti.id ? unsavedEntity as Matter : null);
            })
            .filter(util.existsFilter);
    }, [props.matters, tieIns, unsavedEntity]);

    /**
     * Manage listing of flat fee buckets and milestones.
     *
     * First, check to see if there is a default bucket.
     * - If there is a default bucket, and it does not have milestones, list that bucket
     * - If there is a default bucket, and it has milestones, choose the first milestone that is NOT marked as earned
     *
     * List all flat fee buckets and milestones.
     * - Buckets with no milestones will just use the bucket name and provide a flatFee
     * - Buckets with milestones will read Bucket Name: Milestone Name, and will have both a flatFee and milestone bucket
     *
     * // ERROR: We can't have multiple attached data values in an OptionSelect!
     *
     * @param {Task} [task]
     * @return {FlatFeeSelectItem[]}
     */
    const getBuckets = useCallback((task?: Task): FlatFeeSelectItem[] => {
        const matter = taskMatters[0];
        if (!isTask || !matter) return [];

        const results: FlatFeeSelectItem[] = [];

        let flatFees = props.flatFees.getMappingFor('matterId', matter.id).sort(util.sortByPosition);
        if (flatFees.length === 0) flatFees = props.flatFees.filter(ff => ff.entityId === matter.id);
        const flatFeeToMilestoneMap = new Map();

        flatFees
            .sort(util.sortByPosition)
            .map(flatFee => {
                // Is there an associated contact?
                const contact = flatFee.relatedTo ? formatFML(props.contacts.get(flatFee.relatedTo) || (unsavedEntity?.id === flatFee.relatedTo ? unsavedEntity as Contact : null), true) : '';

                // Any milestones?
                const milestones = props.flatFeeMilestones.getMappingFor('flatFeeId', flatFee.id).sort(util.sortByPosition);
                flatFeeToMilestoneMap.set(flatFee.id, milestones);

                const label = contact ? `${flatFee.name} (${contact})` : flatFee.name;

                if (milestones.length === 0) {
                    results.push({
                        label: label,
                        value: label,
                        data: {flatFee, milestone: null}
                    });
                }
                else {
                    milestones.forEach(milestone => {
                        // One label for each milestone
                        results.push({
                            label: label + `: ${milestone.name}`,
                            value: label + `: ${milestone.name}`,
                            data: {flatFee, milestone}
                        });
                    });
                }
            });

        if (task) {
            // Check to see if we have a flatFeeBucket only. If so, assign it the first milestone bucket.
            const milestones = task.flatFeeBucket ? flatFeeToMilestoneMap.get(task.flatFeeBucket) : [];

            // If we have a flat fee bucket and no milestone bucket, and we have milestones, assign the first milestone bucket to the task.
            if (task.flatFeeBucket && !task.flatFeeMilestoneBucket && milestones?.length) {
                const taskClone = current.clone() as Task;
                taskClone.flatFeeMilestoneBucket = milestones[0].id;
                setCurrent(taskClone);
                setHasChanged(true);
            }

            // Now return the results.
            return results.filter((item: FlatFeeSelectItem) => {
                return (task.flatFeeBucket === item.data?.flatFee?.id) &&
                    (task.flatFeeMilestoneBucket
                     ? task.flatFeeMilestoneBucket === item.data?.milestone?.id
                     : !item.data?.milestone?.id);
            });
        }
        else return results;
    }, [current, isTask, props.contacts, props.flatFeeMilestones, props.flatFees, setHasChanged, taskMatters, unsavedEntity]);

    /**
     * Get activity types valid based on the categories (and their related areas)
     *
     * @param {TaskLike} selTask
     * @return {SelectOption<string, ActivityType>[]>}
     */
    const getActivityTypes = useCallback((selTask?: TaskLike): SelectOption<string, ActivityType>[] => {
        // We only want to show activityTypes that make sense for this specific task.
        // We need to look at the task's categories and find their related system labels.
        // Then we filter our any activity type whose activity category does not include that label.

        // Retrieves a list of system labels, like Administrative, Marketing, Sales, etc. Each category can have multiple related areas.
        const systemLabelsForTask = current.categories
            .map(cid => props.categories.get(cid)?.relatedAreas)
            .filter(existsFilter)
            .flat()
            .filter(distinctFilter);

        const results = props.activityTypes
            .filter(util.deletedFilter)
            .filter(activityType => {
                // Return only activityTypes related to the selected categories' relatedAreas (systemLabels). e.g. only show Administrative activity types for Administrative tasks.
                const activityCategory = props.activityCategories.get(activityType.activityCategoryId ?? '');

                if (activityCategory) {
                    return !!systemLabelsForTask.includes(activityCategory.relatedArea ?? '');
                }
                else {
                    console.warn('Found no activityCategory for activityType', activityType.name);
                    return false;
                }
            })
            .sort((a, b) => {
                // Order by category name, then activity name
                const categoryNameA = props.activityCategories
                    .filter(util.deletedFilter)
                    .find(ac => ac.id === a.activityCategoryId)?.name;
                const categoryNameB = props.activityCategories
                    .filter(util.deletedFilter)
                    .find(ac => ac.id === b.activityCategoryId)?.name;

                const fullNameA = `${categoryNameA} | ${a.name}`;
                const fullNameB = `${categoryNameB} | ${b.name}`;

                return fullNameA.localeCompare(fullNameB);
            })
            .map(activityType => {
                // Need the category name for this activityType
                const activityCategory = props.activityCategories
                    .filter(util.deletedFilter)
                    .find(ac => ac.id === activityType.activityCategoryId);

                if (!activityCategory) console.warn('Found no activityCategory for activityType', activityType.name);

                return {
                    label: <ActivityLabel category={activityCategory} type={activityType}/>,
                    value: `${activityCategory?.name} ${activityType.name}`,
                    data: activityType
                };
            });

        if (selTask) return results.filter(item => selTask.activityTypeIds.includes(item.data.id));
        else return results;
    }, [current.categories, props.activityCategories, props.activityTypes, props.categories]);

    // Handlers
    // onBeginEdit / onEndEdit come from useSubEditActive (see above) and are
    // wired to DocumentBlock; no local counter bookkeeping needed here.

    /**
     * Update activity types
     */
    const onActivityTypeChange = useCallback((values: (SelectOption<string, string>)[]): void => {
        const taskClone = current.clone();
        const isTemplate = taskClone instanceof TemplateTask;

        // Update user and timestamp for save
        taskClone.addedBy = getCurrentUserId();
        taskClone.addedOn = newHRTimestamp();

        if (values?.length > 0) {
            const activityTypes = values.map(v => new ActivityType(v.data));

            // Add the new activity type(s)
            taskClone.activityTypeIds = activityTypes.map(at => at.id);

            // If there are template notes, we should add them.
            const hasTemplateNotes = activityTypes.some(at => !!stripHtml(at.templateNotes || '').result);
            const templateNotes = hasTemplateNotes ? activityTypes
                .filter(at => !!at.templateNotes) // only activities with a template
                .map(at => (at.templateNotes ?? '')
                        .replaceAll('&nbsp;', ' ')
                        .replaceAll('<br>', '\n\r')
                        .replaceAll('<div>', '\n\r')
                        .replaceAll('<p>', '\n\r')
                    || '')
                .map(s => stripHtml(s).result) // strip HTML out if there is any
                .map(s => s.trimEnd().endsWith('') ? s : s + '.') // Hopefully they added periods at the end!
                .join(' ') : '';

            // Get an HTML-stripped version of the current notes
            const strippedNotes = stripHtml(taskClone.invoiceNotes || '').result || '';

            // If there are already invoiceNotes, compare with the new template. If they are different, offer to replace them.
            if (!!strippedNotes && hasTemplateNotes && (strippedNotes !== templateNotes)) {
                // Ask if they want to accept the template's notes
                confirmWarningOkCancel('Overwrite Description?',
                    <div>
                        <p>Would you like to overwrite the existing notes with the template&#39;s notes?</p>
                        <p><b>Template Notes</b></p>
                        {templateNotes}
                    </div>,
                    'Yes, Overwrite with Template',
                    'No, Keep Current Notes',
                    () => {
                        taskClone.invoiceNotes = templateNotes;

                        setHasChanged(true);
                        setInvoiceNotesCounter(prev => prev + 1);
                        setCurrent(taskClone);

                        if (!isTemplate) startEditingPageF('task', current.id);
                    },
                    () => {
                        // Still need to save the new activity types
                        setHasChanged(true);
                        setCurrent(taskClone);

                        if (!isTemplate) startEditingPageF('task', current.id);
                    });
            }
            // If there were no existing notes but there were templates, add them
            else if (!strippedNotes && templateNotes) {
                taskClone.invoiceNotes = templateNotes;

                setHasChanged(true);
                setCurrent(taskClone);
                setInvoiceNotesCounter(prev => prev + 1);

                if (!isTemplate) startEditingPageF('task', current.id);
            }
            else {
                setHasChanged(true);
                setCurrent(taskClone);

                if (!isTemplate) startEditingPageF('task', current.id);
            }
        }
        else { // Empty, clear it out
            taskClone.activityTypeIds = [];

            setHasChanged(true);
            setCurrent(taskClone);
            if (!isTemplate) startEditingPageF('task', current.id);
        }
    }, [current, setHasChanged]);

    const onCopyTextFromTitle = useCallback(() => {
        const task = current.clone();
        const isTemplate = task instanceof TemplateTask;

        task.invoiceNotes = task.title;
        task.addedBy = getCurrentUserId();
        task.addedOn = newHRTimestamp();

        if (!isTemplate) startEditingPageF('task', current.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    const onDescriptionChange = useCallback((_: SyntheticEvent, __: string, value: string) => {
        if (current.invoiceNotes === value) return;

        const task = current.clone();
        const isTemplate = task instanceof TemplateTask;
        task.invoiceNotes = value;

        if (!isTemplate) startEditingPageF('task', task.id);
    }, [current]);

    const onInvoiceNotesBlur = useCallback((_: SyntheticEvent, __: string | null, value: string) => {
        if (current.invoiceNotes === value) return;

        const task = current.clone();
        task.invoiceNotes = stripHtml(value || '').result;
        task.addedOn = newHRTimestamp();
        task.addedBy = getCurrentUserId();

        const isTemplate = task instanceof TemplateTask;
        if (!isTemplate) startEditingPageF('task', task.id);

        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * Render a money input with a checkbox and override field.
     *
     * @param {'money'|'time'} type
     * @param {string} paramName - e.g. laborRate
     * @param {string|ReactElement} uiLabel
     * @param {boolean} [disabled] - should the input be disabled (only override?)
     * @param {string} [checkboxName] - override the checkbox text
     * @returns {ReactElement}
     */
    const renderWithOverride = useCallback((type: 'money' | 'time', paramName: string, uiLabel: string | ReactElement, disabled: boolean = false, checkboxName?: string): ReactElement => {
        const task = current as Task;
        const finalized = task.invoiceId ? FinancialHelper.getFinalizedByInvoiceId(task.invoiceId) : false;

        const isRateField = paramName === 'laborRate' || paramName === 'travelRate';
        const isOverridden = task[`is${paramName.charAt(0).toUpperCase() + paramName.slice(1)}Overridden`];
        const tParam = isRateField && !isOverridden
                       ? (task.associatedRate || getBillableRate(task) || 0)
                       : task[paramName];
        const tParamOverride = task[`${paramName}Override`];

        const value = type === 'money'
                      ? (tParam == null || isNaN(Number(tParam))) ? 0 : Number(tParam).toFixed(2)
                      : (tParam == null || isNaN(Number(tParam))) ? '00:00:00' : formatDuration(tParam, "hh:mm:ss") || '00:00:00';

        const overrideValue = isNaN(tParamOverride) ? 0 : tParamOverride || 0;

        let input: ReactElement | null = null;

        if (disabled) {
            // Disabled
            if (type === 'money') {
                // Disabled and type is money
                input = <div className='money-disabled'>{value}</div>
            }
            else {
                // Disabled and type is duration
                input = <div className='time-disabled'>{value}</div>
            }
        }
        else {
            // Not disabled
            if (type === 'money') {
                // Not disabled and type is money
                input = <input className='money'
                               name={paramName}
                               disabled={finalized}
                               value={value}
                               type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                               onChange={(e) => handleTextChange(e, paramName)}
                />
            }
            else {
                // Not disabled and type is duration
                if (!finalized) {
                    input = <DurationEntry onChange={(value: number) => {
                        if (value !== task.duration) handleDurationChange(`${paramName}`, value)
                    }}
                                           showSeconds={true}
                                           value={task[`${paramName}`]}
                    />
                }
            }
        }

        return (
            <div className='Row task-item--wrap' key={paramName}>

                {/* Label and Input */}
                <div className={`FormComponent task-item__${paramName}AndOverride`}>

                    {/* Label */}
                    <label htmlFor={paramName} className="FormComponentLabel">{uiLabel}</label>

                    {/* Input with [$] for money */}
                    <div className="FormComponentValue NoWrap">

                        <div className='task-item__icon-box'>

                            {/* [$/hms] */}
                            <div className={type === "money" ? "dollar-sign" : "time-sign"}>
                                {type === "money" ? "$" : <IconButton type={Icons.CLOCK}/>}
                            </div>

                            {/* Input */}
                            {input}
                        </div>

                        <span style={{width: '5px'}}/>

                        {/* Override Checkbox*/}
                        <Checkbox checked={task[`is${upFirstLetter(paramName)}Overridden`]}
                                  style={checkBoxMarginLeft}
                                  onClick={(e: SyntheticEvent, data?: any) => {
                                      overrideHandlers[paramName]?.(e, data);
                                  }}
                                  label={checkboxName || 'Override'}
                                  override={true}
                                  disabled={FinancialHelper.getFinalizedByInvoiceId(task.invoiceId ?? '')}
                                  name={`is${upFirstLetter(paramName)}Overridden`}
                        />

                        {/* If overridden, so the override field */}
                        {task[`is${upFirstLetter(paramName)}Overridden`] &&
							<div className='task-item__icon-box'>
                                {/* Override [$/hms] */}
								<div className={type === "money" ? "dollar-sign" : "time-sign"}>
                                    {type === "money"
                                     ? "$"
                                     : <IconButton type={Icons.CLOCK}/>}
								</div>

                                {/* Override Input */}
                                {type === 'money' ?
                                 <input className='money-override'
                                        ref={overrideRefs[`${paramName}OverrideRef`]}
                                        name={paramName}
                                        disabled={finalized}
                                        value={overrideValue}
                                        type="number" step="0.01" min="-9999999999.99" max="9999999999.99"
                                        onChange={(e) => handleTextChange(e, `${paramName}Override`)}
                                 /> :
                                    // <ValidatedInput
                                    //     className='time-override'
                                    //     type='duration'
                                    //     autoComplete='nope'
                                    //     showErrorMessage={false}
                                    //     showValidatedIcon={false}
                                    //     defaultValue={formatDuration(task[`${paramName}Override`] || 0)}
                                    //     onValid={(value) => handleDurationChange(`${paramName}Override`, value)}
                                    // />
                                 <DurationEntry onChange={(value: number) => handleDurationChange(`${paramName}Override`, value)}
                                                showSeconds={true}
                                                value={overrideValue}
                                                doAutofocus={true}
                                 />
                                }
							</div>
                        }
                    </div>
                </div>
            </div>
        );
    }, [current, getBillableRate, handleDurationChange, handleTextChange, overrideHandlers, overrideRefs]);

    const onNotificationChange = useCallback((notification: TaskNotification) => {
        const task = current.clone() as Task;

        if (!deepEqual(notification, task.notification)) {
            task.notification = notification;
            setHasChanged(true);
            setCurrent(task);
        }
    }, [current, setHasChanged]);

    const onEmojiChange = useCallback((emojis: string[]) => {
        const task = current.clone() as Task;
        task.emojis = emojis;
        setHasChanged(true);
        setCurrent(task);
    }, [current, setHasChanged]);

    /**
     * If the task is finalized, we will lock down certain fields, as it would cause changes in the invoice.
     */
    const finalized = useMemo(() => isTemplate ? false : FinancialHelper.getFinalizedByInvoiceId(task?.invoiceId ?? ''), [isTemplate, task?.invoiceId]);

    /**
     * element wrapper for displaying task fields.
     */
    const element = useMemo(() => new FormElement('task-item', current, handleTextChange), [current, handleTextChange]);

    /**
     * To determine if the task has been modified, we can either look at the hasChanged flag (for template tasks),
     * or check additional flags for a normal task.
     */
    const modified = isTemplate
                     ? hasChanged
                     : ((task?.addedOn ?? 0) - (props.current?.addedOn ?? 0) || 1) > 0 || hasChanged;

    /**
     * Get the user or group this is assigned to, or set to current user as default.
     */
    const assignToUser = TaskHelper.getAssigned(current) || getCurrentUser();

    /**
     * Used for notification state flag.  This indicates I am an owner (firm owner, not task owner)
     */
    const iAmFirmOwner = Auth.isOwner();

    /**
     * Used for notification state flag.  This indicates that I created the notification.
     */
    const iAmAssigner = getCurrentUserId() === task?.notification?.from;

    /**
     * Used for notification state flag.  This indicates that I am the recipient of the notification.
     */
    const iAmRecipient = (getCurrentUserId() === task?.notification?.to) ||
        (task?.assignedToType === 'G' && Auth.isInGroup(task?.assignedTo ?? ''));

    /**
     * This is the default value for the Assigned To field.
     */
    const assignToValue = current?.assignedToType === 'U' ? {
        label: <IconButton type={Icons.USER} label={formatUserName(assignToUser as User)}/>,
        value: formatUserName(assignToUser as User),
        data: assignToUser
    } : task?.assignedToType === 'G'
        ? {
            label: <IconButton type={Icons.GROUP} label={(assignToUser as Group)?.name} color='navy' textColor='navy'/>,
            value: (assignToUser as Group)?.name,
            data: assignToUser
        } : {
            label: <IconButton type={Icons.USER} label={formatUserName(assignToUser as User)}/>,
            value: formatUserName(assignToUser as User),
        }

    /**
     * Is this task related to a marketing campaign?
     */
    const marketingRelated = relatedAreas.includes('Marketing');

    /**
     * Is this task related to a Sales Lead?
     */
    const salesRelated = relatedAreas.includes('Sales');

    /**
     * Is this task related to a client task?
     */
    const clientRelated = relatedAreas.includes('Client');

    // const humanResourcesRelated = relatedAreas.includes('Human Resources');
    // const analyticsRelated = relatedAreas.includes('Analytics');
    // const administrativeRelated = relatedAreas.includes('Administrative');
    // const financialsRelated = relatedAreas.includes('Financials');

    /**
     * If related to a case, check to see if the case is closed
     */
    const caseClosed = clientRelated ? matterTieIns
                                         .some(lvm => ['c', 'w'].includes(lvm!.matter.disposition))
                                     : false;

    /**
     * If we have categories with related areas (tieIns) that require popping in a new field, are they filled in?
     */
    // OWA-829: a Lead and a Case are alternative billing containers, never both on one task, so when the
    // categories relate to both Sales and Client either one satisfies the requirement.
    const billingTieInComplete = (salesRelated && clientRelated)
                                 ? (salesTieIns.length > 0 || matterTieIns.length > 0)
                                 : (salesRelated ? salesTieIns.length > 0 : true) &&
                                   (clientRelated ? matterTieIns.length > 0 : true);
    const tieInsComplete = isTemplate
                           ? true
                           : (marketingRelated ? marketingTieIns.length > 0 : true) && billingTieInComplete;

    /**
     * Should we enforce choosing a matter/lead/campaign before saving?
     */
    const enforceTieInsComplete = optionHelper.getValue(OptionNames.ENFORCE_TIE_INS_COMPLETE) as boolean;

    /**
     * Should we enforce selecting a category before saving?
     */
    const enforceActivityType = optionHelper.getValue(OptionNames.ENFORCE_ACTIVITY_TYPE) as boolean;

    /**
     * Should we enforce selecting a category before saving?
     */
    const enforceCategorySelected = optionHelper.getValue(OptionNames.ENFORCE_CATEGORY_SELECTED) as boolean;

    /**
     * Check to see if there are no activity types. Used with enforceActivityType.
     */
    const noActivityTypes = task?.categories.every(cid => {
        const category = props.categories.get(cid);
        if (category) return !category.relatedAreas || category.relatedAreas?.length === 0;
        else return true;
    });

    /**
     * Does this task have a related case? Used to determine if showBillableColumn should tripped.
     */
    const hasRelatedCase = task && taskMatters.length > 0;

    /**
     * Does this task have a related lead? Used to determine if showBillableColumn should be tripped.
     */
    const hasRelatedLead = task && getSalesLeads().length > 0;

    /**
     * Only show billable section if there is a related lead or case (or if this is a template).
     */
    const showBillableColumn = (isTemplate || hasRelatedCase || hasRelatedLead);

    /**
     * If this case is related to a matter, which matter is it?
     */
    const matter = hasRelatedCase ? matterTieIns[0]?.matter : null;

    /**
     * Is this a billable case (all cases are billable, unless they are templates).
     */
    const isBillableCase = isTemplate ? false : current.billable && !!matter && !!matter.rateType;

    /**
     * Is this task related to a matter with flat fees? If so we show the flat fee bucket.
     */
    const isFlatFee = current.billable && isBillableCase && matter?.rateType.includes('f');

    /**
     * Is this task related to a flat-fee ONLY matter? If so we make sure the flat fee bucket is filled in.
     */
    const flatFeeOnly = !!isFlatFee && matter?.rateType === 'f';

    /**
     * If we have selected a personal category, then there will be no activityTypeId.
     */
    const personalActivityOnly = relatedAreas.includes('Personal') && relatedAreas.length === 1;

    // tests for disabled ladder
    const notModified = !modified;
    const noTitle = (current.title?.length ?? 0) === 0;
    const tieInsIncomplete = isTemplate ? false : enforceTieInsComplete && !tieInsComplete;
    const noCategories = enforceCategorySelected && current.categories.length === 0;
    const needsActivityTypes = enforceActivityType ? !personalActivityOnly && !noActivityTypes && current.activityTypeIds.length === 0 : false;
    const shortBillableDescription = !!task ? task.billable && !!task.invoiceNotes && (task.invoiceNotes.length < 6) : false;
    const noFlatFee = flatFeeOnly && !task?.flatFeeBucket;
    const taskInvalid = !!task && !task.isValid();

    /**
     * If the task is not clear to finish, disable the OK button in the OkCancel.
     */
    const disabled =
        !hasChanged ||
        isEditingTimer ||
        notModified ||
        noTitle ||
        tieInsIncomplete ||
        noCategories ||
        needsActivityTypes ||
        shortBillableDescription ||
        noFlatFee ||
        taskInvalid;

    let tieInType = '';
    if (!isTemplate && enforceTieInsComplete && tieInsIncomplete) {
        // Name the requirement that actually failed, not merely an applicable area.
        if (!billingTieInComplete) {
            if (salesRelated && clientRelated) tieInType = 'Lead or Case';
            else if (salesRelated) tieInType = 'Lead';
            else tieInType = 'Case';
        }
        else if (marketingRelated && marketingTieIns.length === 0) tieInType = 'Campaign';
    }

    const messages: Array<[condition: boolean, message: string]> = useMemo(() => [
        [isEditingTimer, 'Timer in edit'],
        [notModified, 'No changes made'],
        [noTitle, 'Title is empty'],
        [noCategories, 'Category required'],
        [tieInsIncomplete, `${tieInType} missing`],
        [needsActivityTypes, 'Activity type required'],
        [shortBillableDescription, `Inadequate billable description (${task?.invoiceNotes?.length || 0} chars provided, 6 required)`],
        [noFlatFee, 'No flat fee selected'],
        [taskInvalid, task?.validationError ?? ''],
    ], [isEditingTimer, needsActivityTypes, noCategories, noFlatFee, noTitle, notModified, shortBillableDescription, task, taskInvalid, tieInType, tieInsIncomplete]);

    /**
     * If Ok is disabled, we need to tell the user why!
     */
    const disabledMessage = useMemo(() => failureLadder(messages), [messages]);

    /**
     * If we are expanded, unset the minHeight/minWidth
     */
    const style: CSSProperties = isContentExpanded ? UNSET_HEIGHT : {};

    /**
     * The OkCancel bar. Used in two places so we consolidate it here.
     */
    const okCancel = <OkCancel label={isTemplate ? 'Task Template' : 'Task'}
                               disabled={disabled}
                               disabledMessage={disabledMessage}
                               useDisabledMessage={true}
                               onClick={onOkClick}
                               onCancel={onCancel}
                               onDelete={!isNew && !isTemplate && !task?.invoiceId ? onDeleteTask : undefined}
                               onTop={true}
    />;

    /**
     * If this case is red-rubber-banded, we set a className to turn the background pink.
     */
    const redRubberBand = useMemo(() => isBillableCase
                                        && clientRelated
                                        && task
                                        && !task?.completionDate
                                        ? matter?.rateType !== 'c'
                                          ? FinancialHelper.getRedRubberBandInfo(matter!).warningColor
                                          : ''
                                        : ''
        , [clientRelated, isBillableCase, matter, task]);

    /**
     * If we are showing the Billable section, we want to wrap it on smaller screens. This creates a className
     * to do that.
     */
    const splitClassName = showBillableColumn ? 'task-item__billable-split split-row' : '';

    /**
     * Additional styles for split classes.
     * TODO: Shouldn't this just go into the splitClassName's styles?
     */
    const splitClassStyle: CSSProperties = (splitClassName && !props.useSmallDisplay && width > 800)
                                           ? FLEX_NO_WRAP
                                           : {};

    /**
     * If this task is related to a finalized invoice, create a message for it.
     */
    let invoiceNumberAndDate = '';
    if (!isTemplate && task?.invoiceId) {
        const invoice = FinancialHelper.getInvoiceById(task?.invoiceId);
        if (invoice) invoiceNumberAndDate = `${invoice.invoiceNumber} on ${DateUtil.formatDate(invoice.issueDate)}`;
    }

    const relatedMatterIds = useMemo(() => {
        return task ? taskMatters.map(m => m.id) : [];
    }, [taskMatters, task]);

    /**
     * We create the form fields here, so that we can hide them if the Notes and Attachments or Private Conversations
     * is expanded.
     */
    const children = (
        <>
            {/* Button bar */}
            {props.inAssociatedTasks && !isContentExpanded && okCancel}

            {/*Form fields */}
            <form className="Form"
                  onSubmit={onOkClick}
                  ref={formRef}
            >
                <div className='main-content'
                     style={{visibility: isContentExpanded ? 'hidden' : 'visible'}}>
                    {/* If this is related to a closed case, put a warning */}
                    {caseClosed && <div className='task-item__case-closed'>
						<b>Warning</b>: This task is related to a closed case. Do not add billable time to it!
					</div>}

                    {/* OWA-998: saves of this task the server refused as stale, held for the user to merge or abandon */}
                    {task && <HeldTaskBanner taskId={task.id}/>}

                    {/* Timer */}
                    <div className='Row task-item__timer-row'>
                        {/* If a timer is running, show the clock */}
                        {task && <TaskTimer task={task}
						                    onAcceptTimerValue={onAcceptTimerValue}
						                    onCompletionToggled={onCompletionToggled}
						                    onEditStateChange={onTimerStateChange}
						                    onChangeCompletionDate={onChangeCompletionDate}
						                    onPauseTimer={onPauseTimer}
						                    onStartTimer={onStartTimer}
						/>}

                        {/* Flag for Notification */}
                        {task && !task.notification?.status && iAmFirmOwner &&
							<div className="task-item__notification-flag">
								<NotificationFlag notification={task.notification || {} as TaskNotification}
								                  target={task.assignedTo ?? task.createdBy ?? ''}
								                  onChange={onNotificationChange}
								                  flagOnly={true}
								                  isGroup={task.assignedToType === 'G'}
								/>
							</div>}

                        {/* Emojis */}
                        {task
                            && OptionHelper.getOptionValueStatic(OptionHelper.TASK_EMOJIS)
                            && <EmojiBar onChange={onEmojiChange}
							             values={task.emojis}
							/>}

                        {isTemplate && <div><b>Template</b></div>}
                    </div>

                    {task && task.notification?.status && (iAmAssigner || iAmRecipient || iAmFirmOwner) &&
                        element.renderElement('notification',
                            <HelpMessage module='task' field='notification'/>,
                            <div className="task-item__notification-flag">
                                <NotificationFlag notification={task.notification || {} as TaskNotification}
                                                  target={task.assignedTo ?? task.createdBy ?? ''}
                                                  onChange={onNotificationChange}
                                                  isGroup={task.assignedToType === 'G'}
                                />
                            </div>)
                    }

                    {(!props.hideFinancialSummary && matter) ?
                     element.renderElement('financials',
                         <HelpMessage module='task' field='caseFinancialSummary'/>,
                         <CaseFinancialSummary matter={matter} showLifeTimeStats={true}/>
                     ) : null}

                    {/* Favorite button if template */}
                    {templateTask && <Checkbox onClick={onFavoriteClick}
					                           id={'make-a-favorite'}
					                           label='Make a Favorite'
					                           reversed={true}
					                           checked={templateTask.favoritedBy.includes(getCurrentUserId())}
					/>}
                    <div className={splitClassName} style={splitClassStyle}>
                        {/* Information leading up to billables */}
                        <div className={showBillableColumn ? 'task-item__left-column' : ''}>
                            {/* (Editable) Title - whole line. If a new task, use a creatable dropdown */}
                            {task && !fromTemplate && !manualTitle
                             ? element.renderElement('title', <HelpMessage module='task' field='title'/>,
                                    <Select name={`task-item-title-select-${task.id}`}
                                            key={`title-select-${task.id}-${task.title}`}
                                            autoAdvance={true}
                                            autoFocus={isNew}
                                            allowNone={true}
                                            allowCreate={true}
                                            formatCreateLabel={() => current.title}
                                            options={getTemplates()}
                                            defaultValue={templateTask ?
                                                {
                                                    label: templateTask.title,
                                                    value: templateTask.title,
                                                    data: templateTask
                                                } : task.title ? {
                                                    label: task.title,
                                                    value: task.title,
                                                    data: task
                                                } : undefined
                                            }
                                            onEmptyInput={onEmptyInput}
                                            onBlur={onTemplateBlur}
                                            onCreateOption={onTemplateCreate}
                                            onChange={onTemplateChange} // Called when a selection is made from a drop-down
                                            onInputChange={onTemplateTextChange} // Called when text is entered into the Select input
                                            isMulti={false}
                                            usesExtraFields={true}
                                            required={true}
                                            ref={titleRef}
                                    />
                                )
                             : element.renderInputOptions({
                                    paramName: 'title',
                                    uiLabel: <HelpMessage module='task' field='title'/>,
                                    type: 'text',
                                    required: true,
                                    autoFocus: isNew,
                                    placeholder: Task.getSchema().fields.title.comment
                                })
                            }

                            {!current.billable && <div className='task-item__description'>
                                {element.renderElement('invoiceNotes',
                                    <HelpMessage module='task' field='description'/>,
                                    <MultilineText id={`task-${current.id}`}
                                                   ref={descriptionRef}
                                                   onBlur={onInvoiceNotesBlur}
                                                   onChange={onDescriptionChange}
                                                   data={current}
                                                   value={current.invoiceNotes ?? ''}
                                                   stripHtml={true}
                                                   isEditable={true}
                                                   disabled={false}
                                    />,
                                    false
                                )}
							</div>
                            }

                            {/* Due Date */}
                            {finalized && task
                             ? element.renderElement('dueDate',
                                    <HelpMessage module='task' field='dueDate'/>,
                                    DateUtil.formatDateTime(task.dueDate, true),
                                    true)
                             : element.renderDateTime('dueDate',
                                    <HelpMessage module='task' field='dueDate'/>,
                                    onDateChange,
                                    false,
                                    true
                                )
                            }

                            {/* Impact */}
                            {element.renderElement('impact',
                                <HelpMessage module='task' field='impact'/>,
                                <LowMedHigh defaultValue={current.impact}
                                            onChange={(e: MouseEvent, v: string) => onChange(e, 'impact', v)}
                                            labelValues={[
                                                i18n.ls('task', 'lowImpact'),
                                                i18n.ls('task', 'mediumImpact'),
                                                i18n.ls('task', 'highImpact')
                                            ]}
                                            labelSizes={['6em', '6em', '7em']}
                                            labelTooltips={[
                                                i18n.d('task', 'lowImpact'),
                                                i18n.d('task', 'mediumImpact'),
                                                i18n.d('task', 'highImpact')
                                            ]}
                                />,
                                true
                            )}

                            {/* Urgency */}
                            {element.renderElement('urgency',
                                <HelpMessage module='task' field='urgency'/>,
                                <LowMedHigh defaultValue={current.urgency}
                                            onChange={(e: MouseEvent, v: string) => onChange(e, 'urgency', v)}
                                            labelValues={[
                                                i18n.ls('task', 'lowUrgency'),
                                                i18n.ls('task', 'mediumUrgency'),
                                                i18n.ls('task', 'highUrgency')
                                            ]}
                                            labelSizes={['6em', '6em', '7em']}
                                            labelTooltips={[
                                                i18n.d('task', 'lowUrgency'),
                                                i18n.d('task', 'mediumUrgency'),
                                                i18n.d('task', 'highUrgency')
                                            ]}
                                />,
                                true
                            )}

                            {/* Start Time */}
                            {finalized
                             ? task && element.renderElement(
                                'startTime',
                                <HelpMessage module='task' field='startTime'/>,
                                task.startTime ? DateUtil.formatDateTime(task.startTime, true) : '',
                                true
                            )
                             : !isTemplate && element.renderDateTime(
                                'startTime',
                                <HelpMessage module='task' field='startTime'/>,
                                onStartTimeChange,
                                false,
                                false
                            )}

                            {/* Duration */}
                            {element.renderElement('duration',
                                <HelpMessage module='task' field='duration'/>,
                                <>
                                    <DecimalInput name="duration"
                                           style={{marginRight: 0}}
                                           step=".01"
                                           autoComplete="nope"
                                           autoCorrect="off"
                                           placeholder="0.0"
                                           id="duration"
                                           maxLength={5}
                                           value={current.duration}
                                           onValueChange={onDurationChange}
                                    />
                                    <span className="task-item__hours">hours</span>
                                </>
                            )}

                            {/* Categories */}
                            {element.renderElement(
                                'categories',
                                <HelpMessage module='task'
                                             field='categories'
                                />,
                                <MultiSelect id='TaskItem__categories'
                                             ariaLabel="Categories"
                                             available={getCategoryNames()}
                                             hints={categoryHints}
                                             selected={selectedCategoryNames}
                                             orientation="row"
                                             disabled={finalized}
                                             wrap={true}
                                             data={getCategoriesForUser()}
                                             boldHandler={(_: number, data: any) => data ? data.entityType === 'category' : false}
                                             onClick={onCategoryChange}
                                />,
                                enforceCategorySelected
                            )}

                            {/* Additional prompts for marketing campaigns  */}
                            {!isTemplate && marketingRelated && element.renderElement(
                                'campaigns',
                                <HelpMessage module='task' field='campaigns'/>,
                                <Select name='task-item-campaigns'
                                        allowCreate={false}
                                        allowNone={true}
                                        defaultValue={marketingTieIns}
                                        isMulti={true}
                                        placeholder='Related marketing campaigns'
                                        onChange={onMarketingCampaign}
                                        options={marketingCampaigns}
                                        disablePortaling={props.embedded}
                                />,
                                enforceTieInsComplete
                            )
                            }

                            {/* Additional prompts for sales leads */}
                            {task && salesRelated && element.renderElement(
                                'leads',
                                <HelpMessage module='task' field='leads'/>,
                                <Select name='task-item-leads'
                                        allowCreate={false}
                                        allowNone={true}
                                        disabled={finalized}
                                        defaultValue={getSalesLeads(task)}
                                        isMulti={true}
                                        placeholder='Related sales leads'
                                        onChange={onSalesLead}
                                        options={getSalesLeads()}
                                        disablePortaling={props.embedded}
                                />,
                                enforceTieInsComplete
                            )
                            }

                            {/* Additional prompts for client matters */}
                            {task && clientRelated && element.renderElement(
                                'cases',
                                <HelpMessage module='task' field='cases'/>,
                                <CaseSelect name='case-select-task-item'
                                            autoAdvance={true}
                                            isMulti={false}
                                            allowNone={true}
                                            showIcon={true}
                                            disabled={finalized}
                                            onChange={onMatterChanged}
                                            placeholder='Related cases'
                                            defaultValue={relatedMatterIds}
                                            unsavedEntity={props.env?.topLevel === 'matter' ? unsavedEntity as Matter : undefined}
                                            disablePortaling={props.embedded}
                                            hideClosed={props.inAssociatedTasks ? false : isNew || task.fresh || !task.dbAddedOn || !!task.fromTemplateId}
                                />,
                                enforceTieInsComplete
                            )}

                            {/* Activity Type */}
                            {relatedAreas.length > 0 && !personalActivityOnly &&
								<div className="split-row-force">
                                    {element.renderSelect('activityType',
                                        <HelpMessage module='task' field='activityType'/>,
                                        true,
                                        !enforceActivityType || personalActivityOnly,
                                        getActivityTypes,
                                        onActivityTypeChange,
                                        true,
                                        {autoAdvance: true, disablePortaling: props.embedded}
                                    )}
                                    {!props.embedded &&
										<div style={{margin: 'auto 5px auto auto'}}>
											<RoutedIconButton type={Icons.CONFIG}
											                  title='Configure Activity Types'
											                  route='/config/tasks/activities'
											/>
										</div>
                                    }
								</div>
                            }

                            {/* Assign To User or Group */}
                            {(isTemplate || !task?.invoiceId) ?
                             element.renderElement('assignedTo',
                                 <HelpMessage module='task' field='assignedTo'/>,
                                 <>
                                     <UserSelect name='TaskItem__assignedTo'
                                                 autoAdvance={true}
                                                 onClick={onReassign}
                                                 defaultValue={assignToValue.data}
                                                 allowNone={false}
                                                 disabled={finalized || assigneeLocked}
                                                 isMulti={false}
                                                 disablePortaling={props.embedded}
                                                 includeDisabled={!isTemplate && !!props.current}
                                                 includeDeleted={!isTemplate}
                                                 includeGroups={task ? task.elapsedTime === 0 : true}
                                     />
                                     {assigneeLocked && <small>{TASK_ASSIGNEE_LOCKED_MESSAGE}</small>}
                                 </>
                             ) :
                             element.renderElement('assignedTo',
                                 <HelpMessage module='task' field='assignedTo'/>,
                                 <strong>{formatUserNameFromId(current.assignedTo ?? '')}</strong>
                             )}

                            {/* Participants - Uses a dropdown list of contacts */}
                            {task && element.renderElement('participants',
                                <HelpMessage module='task' field='participants'/>,
                                <ContactSelect current={task.relatedTo}
                                               key={`task-item-contact-select-${current.id}`}
                                               name='TaskRelatedTo'
                                               parentId={current.id}
                                               isMulti={true}
                                               onChange={onRelatedTo}
                                               ref={clientsRef}
                                               autoSave={true}
                                               unsavedEntity={props.env?.topLevel === 'contact' ? unsavedEntity as Contact : undefined}
                                               disablePortaling={props.embedded}
                                />
                            )
                            }

                            {/* Private */}
                            {!isTemplate && element.renderCheckbox('private',
                                <HelpMessage module='task' field='private'/>,
                                false,
                                onPrivateClick)
                            }

                            {/* Docket and Room */}
                            {task && hasRelatedCase &&
								<div className="split-row-force">
                                    {element.renderElement('docketId',
                                        <HelpMessage module='task'
                                                     field='docketId'
                                        />,
                                        <DocketSelect onChange={onDocketChange}
                                                      autoAdvance={true}
                                                      allowNone={true}
                                                      defaultValue={task.docketId ?? undefined}
                                                      disablePortaling={props.embedded}
                                        />
                                    )}
                                    {!props.embedded &&
										<RoutedIconButton type={Icons.CONFIG}
										                  title='Configure Dockets'
										                  route='/config/cases/dockets'
										/>
                                    }
								</div>
                            }

                            {/* Room Number */}
                            {task && task?.docketId && element.renderInput('roomNumber',
                                <HelpMessage module='task' field='roomNumber'/>, 'text', false)}

                            {/* Global */}
                            {isTemplate && element.renderCheckbox('global',
                                <HelpMessage module='task' field='global'/>,
                                false,
                                onGlobalClick)
                            }

                            {/* Invoice (if attached) */}
                            {!isTemplate && invoiceNumberAndDate && element.renderElement('invoiceId', 'Invoice',
                                <div>#{invoiceNumberAndDate}</div>)
                            }
                        </div>

                        {/* Billables */}
                        <div className={`task-item__right-column ${current.billable ? 'on' : 'off'}`}>
                            {showBillableColumn &&
								<>
									<div className={`u-row-nowrap task-item__billable ${current.billable ? 'task-item--billable-open' : ''}`}>
										<Checkbox checked={current.billable}
										          onClick={onBillable}
										          label={i18n.ls('task', 'billable')}
										          disabled={finalized}
										          useDelay={true}
										/>
										<HelpMessage module='task' field='billable' hideLabel={true} invertColors={current.billable}/>
									</div>

                                    {/* Rate */}
                                    {current.billable && <div className='task-item__billable-area'>
                                        {/* Invoice Notes */}
										<div className='split-row-force'>
                                            {/* Copy title */}
                                            {(current.invoiceNotes == null || stripHtml(current.invoiceNotes || '').result === '') && !finalized &&
												<IconButton id='TaskItemCopyDown'
												            type={Icons.PASTE}
												            title='Copy text from Title'
												            onClick={onCopyTextFromTitle}
												            tabIndex={-1}
												/>
                                            }

                                            {/* Invoice Notes (called Description) */}
                                            {finalized
                                             ? <div className='task-item__invoice-notes-disabled'>
                                                 <div className='task-item__invoice-notes-disabled-header'>
                                                     {i18n.l('task', 'notesDisabled')}
                                                 </div>
                                                 <div className='u-row'>
                                                     <div className='task-item__invoice-notes-disabled-description'>
                                                         Description
                                                     </div>
                                                     <div className='task-item__invoice-notes-disabled-text'
                                                          style={{whiteSpace: 'pre-wrap'}}
                                                     >
                                                         {current.invoiceNotes}
                                                     </div>
                                                 </div>
                                             </div>
                                             : element.renderElement('invoiceNotes',
                                                    <HelpMessage module='task' field='description'/>,
                                                    <MuiTextField key={`inc_${invoiceNotesCounter}`}
                                                                  id={`task-${current.id}-description`}
                                                                  onBlur={e => onInvoiceNotesBlur(e, null, e.target.value)}
                                                                  onChange={e => onInvoiceNotesChange(e, null, e.target.value)}
                                                                  defaultValue={current.invoiceNotes || ''}
                                                                  multiline={true}
                                                                  type='text'
                                                                  autoComplete='off'
                                                                  slotProps={{
                                                                      htmlInput: {
                                                                          'data-lpignore': 'true',
                                                                          'data-1p-ignore': 'true',
                                                                          'data-bwignore': 'true',
                                                                          'data-form-type': 'other',
                                                                          autoComplete: 'off',
                                                                      },
                                                                  }}
                                                    />,
                                                    false
                                                )}
										</div>

                                        {/* Cost Options */}

                                        {element.renderElement('invoiceOptions',
                                            <HelpMessage module='task'
                                                         field='invoiceOptions'
                                            />,
                                            <div className='task-item__total-options split-row'>
                                                {/* No Charge */}
                                                <div className='task-item__noCharge'>
                                                    <Checkbox checked={current.noCharge}
                                                              disabled={finalized}
                                                              onClick={onNoCharge}
                                                              label={i18n.l('task', 'noCharge')}/>
                                                </div>

                                                {/* Hide from Invoice */}
                                                <div className='task-item__hideFromInvoice'>
                                                    <Checkbox checked={current.hideFromInvoice}
                                                              disabled={finalized}
                                                              onClick={onHideFromInvoice}
                                                              label={i18n.l('task', 'excludeFromInvoice')}/>
                                                </div>
                                            </div>
                                        )}

                                        {/* Labor */}
                                        {!isTemplate &&
											<div className='split-row'>
                                                {/* Labor Rate */}
                                                {renderWithOverride('money',
                                                    'laborRate',
                                                    <HelpMessage module='task' field='laborRate'/>,
                                                    true
                                                )}

                                                {/* Labor Time */}
                                                {renderWithOverride('time',
                                                    'laborTime',
                                                    <HelpMessage module='task' field='laborTime'/>,
                                                    true
                                                )}
											</div>
                                        }

                                        {/* Travel */}
                                        {!isTemplate &&
											<div className='split-row'>
                                                {/* Travel Rate */}
                                                {renderWithOverride('money',
                                                    'travelRate',
                                                    <HelpMessage module='task' field='travelRate'/>,
                                                    true
                                                )}

                                                {/* Travel Time */}
                                                {renderWithOverride('time',
                                                    'travelTime',
                                                    <HelpMessage module='task' field='travelTime'/>,
                                                    true
                                                )}
											</div>
                                        }

                                        {/* Costs/Flat Fees */}
                                        {<div className='split-row-total'>
                                            {/* Total (not editable), Override/Flat Fee, No Charge, Hide From Invoice */}
                                            {renderWithOverride('money', 'cost',
                                                <HelpMessage module='task'
                                                             field='cost'
                                                />,
                                                true, 'Override')}

                                            {/* If this case uses fixed cost, allow categorization into a bucket */}
                                            {isFlatFee ?
                                             element.renderSelect(
                                                 'flatFeeBucket',
                                                 <HelpMessage module='task' field='flatFee'/>,
                                                 false,
                                                 true,
                                                 getBuckets,
                                                 onFlatFeeBucketChange,
                                                 true,
                                                 {disablePortaling: props.embedded}
                                             ) : null
                                            }
                                        </div>}
									</div>
                                    }
								</>
                            }
                        </div>
                    </div>
                </div>

                {/* History */}
                {task && <EntityMeta entityType="task"
	                                        entity={task}
	                                        hidden={!!isContentExpanded}
	                                        onVersionChange={(e) => setCurrent(e as Task)}
	            />}

                {/* Notes and Comments share the screen if it is wide enough */}
                {task && <ContentBlockContainer name={`cbc-task-item-${props.env?.topLevelId ?? ''}-${current.id}`}
				                                       displayName='cbc-task-item'
				                                       onBeforeCollapse={onBeforeCollapse}
				                                       onBeforeExpand={onBeforeExpand}
				                                       showViewToggles={true}
				                                       overrideHeight={'40em'}
				                                       breakWidth={500}
				                                       initialTab={props.documentId ? `cb-task-documents-${task.id}` : undefined}
				>
                    {/* Notes and Documents (replaces attachments) */}
					<ContentBlock key={`cb-task-documents-${task.id}`}
					              title={`cb-task-documents-${task.id}`}
					              displayTitle={<HelpMessage module='document' field='title'/>}
					              customStyles='container--documents'
					              color='black'
					              hasContent={true}
					              icon={Icons.FILE_TEXT.reactIcon}
					>
						<DocumentBlock entityName='task'
						               entityId={task.id}
						               onBeforeEdit={onBeginEdit}
						               onEndEdit={onEndEdit}
						               embedded={props.embedded}
						               documentId={props.documentId}
						/>
					</ContentBlock>

                    {/* Comments */}
					<ContentBlock key={`cb-task-private-discussions-${task.id}`}
					              title={`cb-task-private-discussions-${task.id}`}
					              displayTitle={<HelpMessage module='comment' field='title'/>}
					              customStyles='container--comments'
					              icon={Icons.COMMENTS.reactIcon}
					              hasContent={true}
					>
						<Comments entityId={task.id}
						          entityType='task'
						          isEditing={true}
						          formatRelated={false}
						          embedded={props.embedded}
						/>
					</ContentBlock>

				</ContentBlockContainer>}

                {/* Convert to a template task */}
                {!isTemplate &&
					<div className='task-item__from-template'>
                        {task?.fromTemplateId && TemplateTaskHelper.getSource(task) && <div>
							From Template:&nbsp;<b>{TemplateTaskHelper.getSource(task)?.title}</b>
						</div>
                        }
						<div className='task-item__convert'>
							<Button onClick={convertToTemplate}
							        startIcon={<PublishedWithChanges/>}
							        color='inherit'
							        size='large'
							        sx={{minWidth: 'fit-content', textTransform: 'capitalize', fontSize: 16, fontWeight: "400"}}
							        title={i18n.ls('task', 'convertToTemplate')}
							>
                                {i18n.l('task', 'convertToTemplate')}
							</Button>
							<HelpMessage module='task' field='convertToTemplate' hideLabel={true}/>
						</div>
					</div>
                }

                {/* Button bar */}
                {!props.inAssociatedTasks && !isContentExpanded && okCancel}
            </form>
        </>
    );

    return (props.inAssociatedTasks || props.embedded) ?
           <div className={`task-item ${redRubberBand} ${props.embedded ? 'embedded' : ''}`.trim()}
                style={style}
           >
               {children}
           </div> :
           <ScrollWithIndicator className={`task-item ${redRubberBand}`.trim()} style={style}>
               {children}
           </ScrollWithIndicator>;

}

type TaskItemEnv = Partial<{
    matter: Matter,
    topLevel: 'contact' | 'lead' | 'matter' | 'campaign' | 'task',
    topLevelId: uuid,
    unsavedEntity: Contact | Lead | Matter | Campaign
}>;

export type QuickAction = {
    taskToShow?: Task;
    oldTaskSaved: boolean;
};

export type TaskItemProps = ReturnType<typeof mapStateToProps> &
    SearchPageProps &
    EditableTableCardProps<TaskLike, TaskItemEnv> &
    ContentBlockChildProps &
    {
        // Fields that a new object should use as presets
        presets?: Partial<TaskLike>,

        // Is this task currently within the AssociatedTasks?
        inAssociatedTasks?: boolean,

        // Hide the financial summary?
        hideFinancialSummary?: boolean,

        // The Event object to be manipulated
        current?: TaskLike,

        // Is this card embedded in a modal dialog?
        embedded?: boolean,

        // ID to use for a new instance
        newId?: uuid,

        // Should close warnings be disregarded?
        noCloseWarning?: boolean,

        // Function to call when all editing is finished
        onOk: OkFunction<TaskLike>

        // Function to call if we decide not to save changes
        onCancel: CancelFunction,

        // Unused, part of quick actions
        quickAction?: QuickAction,

        // If set, deep-link to this document within the Documents tab
        documentId?: string,
    };

const mapStateToProps = (state: RootState) => {
    return {
        activityCategories: state.activityCategories,
        activityTypes: state.activityTypes,
        campaigns: state.campaigns,
        caseTypes: state.caseTypes,
        categories: state.categories,
        comments: state.comments,
        contacts: state.contacts,
        dockets: state.dockets,
        flatFees: state.flatFees,
        flatFeeMilestones: state.flatFeeMilestones,
        groups: state.groups,
        leads: state.leads,
        matters: state.matters,
        misc: state.misc,
        quickAction: state.misc.quickAction,
        options: state.options,
        tasks: state.tasks,
        templateTasks: state.templateTasks,
        users: state.users
    };
};

export default connect(mapStateToProps)(TaskItem);
